import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../../api/api';
import * as XLSX from 'xlsx';
import {
  Department,
  EmployeeAttendanceEntry,
  EmployeeScoreBlock,
  EvaluationQuestion,
  KitchenEmployeeDetails,
  KitchenEmployeeSummary,
  HRAttendanceGrid,
  MonthlyRevenue,
} from '../../types';
import { printHtmlInHiddenFrame } from '../../utils/printDocument';
import { extractApiErrorMessage } from '../../utils/apiError';
import {
  OPERATING_GROUP_CHANGED_IN_ANOTHER_TAB_MESSAGE,
  useSelectedOperatingGroup,
} from '../../hooks/useSelectedOperatingGroup';
import { useAppSelector } from '../../store/hooks';
import {
  isOperatingContextReady,
  useOperatingContext,
} from '../../utils/operatingContextLifecycle';
import { isRestaurantOperatingContext } from '../../utils/operatingContext';
import EmployeeDossierPanel from './EmployeeDossierPanel';
import RestaurantPersonnelPage from './RestaurantPersonnelPage';
import './KitchenEmployeesPage.css';

type EmployeeForm = {
  fullName: string;
  position: string;
  positionCoefficient: string;
  correction: string;
  comment: string;
  isActive: boolean;
  departmentId: string;
  useAttendance: boolean;
  useFixedRateSalary: boolean;
  fixedMonthlyRate: string;
  revenuePercent: string;
};

type TabKey = 'employees' | 'attendance' | 'departments' | 'questions' | 'revenue';

const emptyForm: EmployeeForm = {
  fullName: '',
  position: '',
  positionCoefficient: '1',
  correction: '0',
  comment: '',
  isActive: true,
  departmentId: '',
  useAttendance: true,
  useFixedRateSalary: false,
  fixedMonthlyRate: '0',
  revenuePercent: '0',
};

const panelStyle: React.CSSProperties = {
  background: 'var(--crm-surface)',
  color: 'var(--crm-text)',
  border: '1px solid var(--crm-border)',
  borderRadius: 'var(--crm-radius-md)',
  boxShadow: 'var(--crm-shadow-sm)',
};
const buttonStyle = (tone: 'info' | 'success' | 'danger' | 'warning' | 'muted' | 'accent'): React.CSSProperties => ({
  background: `var(--crm-${tone === 'muted' ? 'surface-alt' : tone === 'accent' ? 'accent' : `${tone}-bg`})`,
  color: `var(--crm-${tone === 'muted' ? 'text' : tone === 'accent' ? 'accent-contrast' : tone})`,
  border: `1px solid var(--crm-${tone === 'muted' ? 'border' : tone})`,
  borderRadius: 'var(--crm-radius-sm)',
  padding: '8px 12px',
});

const getAttendanceEntryHours = (entry: HRAttendanceGrid['employees'][number]['cells'][number]['entries'][number]) => {
  if (!entry.checkOutAt) return 0;
  const start = new Date(entry.checkInAt).getTime();
  const end = new Date(entry.checkOutAt).getTime();
  return Math.max(0, (end - start) / 36e5);
};

const getCellHours = (cell: HRAttendanceGrid['employees'][number]['cells'][number]) =>
  cell.hasEntry ? cell.entries.reduce((sum, entry) => sum + getAttendanceEntryHours(entry), 0) : null;

const getCellWeightedHours = (cell: HRAttendanceGrid['employees'][number]['cells'][number]) =>
  cell.entries.reduce(
    (sum, entry) => sum + getAttendanceEntryHours(entry) * Number(entry.hourCoefficient ?? 1),
    0
  );

const blockLabels: Record<string, string> = {
  qualification: 'Кваліфікація',
  productivity: 'Продуктивність',
  responsibility: 'Відповідальність',
};

const padDatePart = (value: number) => String(value).padStart(2, '0');
const getLocalDateInputValue = (date = new Date()) =>
  `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(date.getDate())}`;
const getPreviousLocalMonthInputValue = (date = new Date()) => {
  const previousMonth = new Date(date.getFullYear(), date.getMonth() - 1, 1);
  return `${previousMonth.getFullYear()}-${padDatePart(previousMonth.getMonth() + 1)}`;
};
const getNextLocalDateInputValue = (dateValue: string) => {
  const [year, month, day] = dateValue.split('-').map(Number);
  const date = new Date(year, month - 1, day + 1);
  return getLocalDateInputValue(date);
};
const toLocalDateTimeString = (dateValue: string, timeValue: string) => `${dateValue}T${timeValue}:00`;

const LegacyKitchenEmployeesPageContent: React.FC = () => {
  const [tab, setTab] = useState<TabKey>('employees');
  const [employees, setEmployees] = useState<KitchenEmployeeSummary[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [details, setDetails] = useState<KitchenEmployeeDetails | null>(null);
  const [attendanceGrid, setAttendanceGrid] = useState<HRAttendanceGrid | null>(null);
  const [gridDepartmentId, setGridDepartmentId] = useState('');
  const [gridMonth, setGridMonth] = useState(getPreviousLocalMonthInputValue());
  const [gridSavingCell, setGridSavingCell] = useState<string | null>(null);
  const [editingGridCell, setEditingGridCell] = useState<{
    appUserId: string;
    workDate: string;
    fullName: string;
    departmentName: string;
    entries: HRAttendanceGrid['employees'][number]['cells'][number]['entries'];
    entryId: number | null;
    checkInAt: string;
    checkOutAt: string;
    checkOutNextDay: boolean;
    hourCoefficient: string;
    comment: string;
  } | null>(null);
  const [gridSearch, setGridSearch] = useState('');
  const [gridSort, setGridSort] = useState<'name-asc' | 'name-desc' | 'hours-desc' | 'hours-asc'>('name-asc');
  const [printExpandedAttendanceWithHours, setPrintExpandedAttendanceWithHours] = useState(false);
  const [printExpandedAttendanceNoHours, setPrintExpandedAttendanceNoHours] = useState(false);
  const [form, setForm] = useState<EmployeeForm>(emptyForm);
  const [manualCreateMode, setManualCreateMode] = useState(false);
  const [scores, setScores] = useState<EmployeeScoreBlock[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string>('');
  const [editMode, setEditMode] = useState(false);

  const [departments, setDepartments] = useState<Department[]>([]);
  const [questions, setQuestions] = useState<EvaluationQuestion[]>([]);
  const [revenues, setRevenues] = useState<MonthlyRevenue[]>([]);

  const [bulkDate, setBulkDate] = useState(getLocalDateInputValue());
  const [bulkCoeff, setBulkCoeff] = useState('1');
  const [bulkComment, setBulkComment] = useState('');
  const [bulkSelectedIds, setBulkSelectedIds] = useState<string[]>([]);

  // Manual attendance form state
  const [showManualForm, setShowManualForm] = useState(false);
  const [manualForm, setManualForm] = useState({
    workDate: getLocalDateInputValue(),
    checkInAt: '09:00',
    checkOutAt: '18:00',
    hasCheckOut: true,
    hourCoefficient: '1',
    comment: '',
  });

  // Inline edit state for attendance rows
  const [editingAttendanceId, setEditingAttendanceId] = useState<number | null>(null);
  const [editingAttendanceRow, setEditingAttendanceRow] = useState({
    checkInDate: '',
    checkInTime: '',
    checkOutDate: '',
    checkOutTime: '',
    hasCheckOut: true,
    hourCoefficient: '1',
    comment: '',
  });

  const [newDepartment, setNewDepartment] = useState({
    name: '',
    description: '',
    parentId: '',
    salaryFundLimit: '0',
    salaryFundPercent: '0',
  });
  const [editingDepartmentId, setEditingDepartmentId] = useState<number | null>(null);
  const [editingDepartment, setEditingDepartment] = useState({
    name: '',
    description: '',
    parentId: '',
    salaryFundLimit: '0',
    salaryFundPercent: '0',
  });

  const [newQuestion, setNewQuestion] = useState({
    blockType: 'qualification',
    name: '',
    description: '',
    itemType: 'positive',
    isActive: true,
    sortOrder: '0',
  });

  const [revenueForm, setRevenueForm] = useState({
    period: `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`,
    amount: '0',
    isManual: true,
    includeAdditionalServices: true,
    notes: '',
  });
  const [editingRevenueId, setEditingRevenueId] = useState<number | null>(null);

  const [questionPickerByBlock, setQuestionPickerByBlock] = useState<Record<string, string>>({
    qualification: '',
    productivity: '',
    responsibility: '',
  });
  const [importingWorkbook, setImportingWorkbook] = useState(false);
  const [importPreview, setImportPreview] = useState<{
    employees: number;
    questions: number;
    salaryRules: number;
    preview?: {
      employees: Array<{ fullName: string; position: string; monthsWorked: number; positionCoefficient: number; correction: number; fixedCoefficient: number; comment: string }>;
      questions: Array<{ blockType: string; name: string; description: string; itemType: string; sortOrder: number }>;
      salaryRules: Array<{ position: string; positionCoefficient: number }>;
    };
  } | null>(null);
  const [importFile, setImportFile] = useState<File | null>(null);
  const importFileRef = useRef<HTMLInputElement | null>(null);

  const downloadHrTemplate = async () => {
    const { data: blob } = await api.get<Blob>('/export/kitchen-hr-template/excel', { responseType: 'blob' });
    const objectUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = 'kitchen-hr-template.xlsx';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(objectUrl);
  };

  const selectedSummary = useMemo(
    () => employees.find((e) => e.id === selectedId) || null,
    [employees, selectedId]
  );

  const selectedRevenue = useMemo(() => {
    const [yearText, monthText] = (revenueForm.period || '').split('-');
    const year = Number(yearText || 0);
    const month = Number(monthText || 0);
    return revenues.find((r) => r.year === year && r.month === month) || null;
  }, [revenues, revenueForm.period]);

  const selectedDepartment = useMemo(
    () => departments.find((d) => d.id === Number(form.departmentId || details?.departmentId || 0)) || null,
    [departments, details?.departmentId, form.departmentId]
  );

  const departmentSalaryFundPreview = useMemo(() => {
    const revenue = selectedRevenue?.amount ?? 0;
    return revenue * ((selectedDepartment?.salaryFundPercent ?? 0) / 100);
  }, [selectedDepartment?.salaryFundPercent, selectedRevenue]);

  const selectedDepartmentSalaryFundPercent = Number(selectedDepartment?.salaryFundPercent ?? 0);

  const loadDepartments = useCallback(async () => {
    const res = await api.get<Department[]>('/departments');
    setDepartments(res.data);
  }, []);

  const loadQuestions = useCallback(async () => {
    const res = await api.get<EvaluationQuestion[]>('/evaluation-questions');
    setQuestions(res.data);
  }, []);


  const loadRevenues = useCallback(async () => {
    const res = await api.get<MonthlyRevenue[]>('/monthly-revenues');
    setRevenues(res.data);
  }, []);

  const loadDetails = useCallback(async (id: string) => {
    const res = await api.get<KitchenEmployeeDetails>(`/kitchen-employees/${id}`);
    setDetails(res.data);
    setScores(res.data.scoreBlocks || []);
    setForm({
      fullName: res.data.fullName || '',
      position: res.data.position || '',
      positionCoefficient: String(res.data.positionCoefficient ?? 1),
      correction: String(res.data.correction ?? 0),
      comment: res.data.comment || '',
      isActive: !!res.data.isActive,
      departmentId: res.data.departmentId ? String(res.data.departmentId) : '',
      useAttendance: res.data.useAttendance ?? true,
      useFixedRateSalary: res.data.useFixedRateSalary ?? false,
      fixedMonthlyRate: String(res.data.fixedMonthlyRate ?? 0),
      revenuePercent: String(res.data.revenuePercent ?? 0),
    });
    setEditMode(false);
  }, []);

  const loadEmployees = useCallback(async (keepId?: string | null) => {
    setLoading(true);
    try {
      const res = await api.get<KitchenEmployeeSummary[]>('/kitchen-employees');
      setEmployees(res.data);
      const target = keepId ?? (res.data[0]?.id ?? null);
      setSelectedId(target);
      if (target) {
        await loadDetails(target);
      } else {
        setDetails(null);
        setScores([]);
      }
    } finally {
      setLoading(false);
    }
  }, [loadDetails]);

  const loadAttendanceGrid = useCallback(async (departmentId = gridDepartmentId, month = gridMonth) => {
    const params = new URLSearchParams();
    if (departmentId) params.set('departmentId', departmentId);
    if (month) params.set('month', `${month}-01T00:00:00`);
    const res = await api.get<HRAttendanceGrid>(`/kitchen-employees/attendance-grid${params.toString() ? `?${params.toString()}` : ''}`);
    setAttendanceGrid(res.data);
  }, [gridDepartmentId, gridMonth]);

  useEffect(() => {
    if (tab === 'attendance') {
      loadAttendanceGrid().catch(() => setMessage('Не вдалося завантажити табель.'));
    }
  }, [tab, loadAttendanceGrid]);

  const startManualCreateEmployee = () => {
    setManualCreateMode(true);
    setSelectedId(null);
    setDetails(null);
    setScores([]);
    setMessage('');
    setEditMode(true);
    setForm(emptyForm);
  };

  useEffect(() => {
    let active = true;
    setLoadError('');
    const run = async () => {
      await Promise.all([loadDepartments(), loadQuestions(), loadRevenues()]);
      await loadEmployees();
      await loadAttendanceGrid();
    };
    run().catch((error: unknown) => {
      if (active) {
        setLoadError(extractApiErrorMessage(error, 'Не вдалося завантажити кадрові дані.'));
      }
    });
    return () => { active = false; };
  }, [loadAttendanceGrid, loadDepartments, loadEmployees, loadQuestions, loadRevenues]);

  const saveEmployee = async () => {
    if (!selectedId) return;
    setSaving(true);
    setMessage('');
    try {
      const payload = {
        fullName: form.fullName,
        position: form.position,
        positionCoefficient: Number(form.positionCoefficient || 0),
        correction: Number(form.correction || 0),
        comment: form.comment,
        isActive: form.isActive,
        departmentId: form.departmentId ? Number(form.departmentId) : null,
        useAttendance: form.useAttendance,
        useFixedRateSalary: form.useFixedRateSalary,
        fixedMonthlyRate: Number(form.fixedMonthlyRate || 0),
        revenuePercent: Number(form.revenuePercent || 0),
      };

      await api.put(`/kitchen-employees/${selectedId}`, payload);
      await loadEmployees(selectedId);
      setMessage('Збережено.');
    } catch (e: any) {
      setMessage(e?.response?.data?.message || 'Помилка збереження.');
    } finally {
      setSaving(false);
    }
  };

  const archiveEmployee = async () => {
    if (!selectedId) return;
    if (!window.confirm('Архівувати працівника? Табель, оцінки та кадрова історія залишаться збереженими.')) return;
    setSaving(true);
    try {
      await api.delete(`/kitchen-employees/${selectedId}`);
      setMessage('Працівника архівовано, історію збережено.');
      setDetails(null);
      setSelectedId(null);
      await loadEmployees();
      await loadQuestions();
    } catch (e: any) {
      setMessage(e?.response?.data?.message || 'Не вдалося видалити працівника.');
    } finally {
      setSaving(false);
    }
  };

  const saveScores = async () => {
    if (!selectedId) return;
    setSaving(true);
    setMessage('');
    try {
      await api.put(`/kitchen-employees/${selectedId}/scores`, {
        blocks: scores.map((b) => ({
          blockType: b.blockType,
          items: b.items.map((i) => ({
            name: i.name,
            description: i.description,
            score: Number(i.score || 0),
            itemType: i.itemType || 'positive',
          })),
        })),
      });
      await loadEmployees(selectedId);
      setMessage('Оцінки оновлено.');
    } catch (e: any) {
      setMessage(e?.response?.data?.message || 'Помилка збереження оцінок.');
    } finally {
      setSaving(false);
    }
  };

  const addScoreItem = (blockType: string, itemType: string) => {
    setScores((prev) =>
      prev.map((b) =>
        b.blockType === blockType
          ? {
              ...b,
              items: [...b.items, { id: 0, name: '', description: '', score: 0, itemType }],
            }
          : b
      )
    );
  };

  const addQuestionToBlock = (blockType: string) => {
    const selectedQuestionId = Number(questionPickerByBlock[blockType] || 0);
    if (!selectedQuestionId) return;
    const q = questions.find((x) => x.id === selectedQuestionId);
    if (!q) return;
    setScores((prev) =>
      prev.map((b) =>
        b.blockType === blockType
          ? {
              ...b,
              items: [
                ...b.items,
                {
                  id: 0,
                  name: q.name,
                  description: q.description,
                  score: 0,
                  itemType: q.itemType || 'positive',
                },
              ],
            }
          : b
      )
    );
  };

  const updateScoreItem = (blockType: string, index: number, patch: Partial<EmployeeScoreBlock['items'][number]>) => {
    setScores((prev) =>
      prev.map((b) => {
        if (b.blockType !== blockType) return b;
        return {
          ...b,
          items: b.items.map((item, i) => (i === index ? { ...item, ...patch } : item)),
        };
      })
    );
  };

  const removeScoreItem = (blockType: string, index: number) => {
    setScores((prev) =>
      prev.map((b) => (b.blockType === blockType ? { ...b, items: b.items.filter((_, i) => i !== index) } : b))
    );
  };

  const managerCheckIn = async (employeeId = selectedId) => {
    if (!employeeId) return;
    try {
      await api.post(`/kitchen-employees/${employeeId}/attendance/check-in`, {});
      if (selectedId === employeeId) await loadDetails(employeeId);
      await loadAttendanceGrid();
    } catch (e: any) {
      setMessage(e?.response?.data?.message || 'Не вдалося відмітити прихід.');
    }
  };

  const managerCheckOut = async (employeeId = selectedId) => {
    if (!employeeId) return;
    try {
      await api.post(`/kitchen-employees/${employeeId}/attendance/check-out`, {});
      if (selectedId === employeeId) await loadDetails(employeeId);
      await loadAttendanceGrid();
    } catch (e: any) {
      setMessage(e?.response?.data?.message || 'Не вдалося відмітити вихід.');
    }
  };

  const addManualAttendance = async () => {
    if (!selectedId) return;
    try {
      const checkIn = toLocalDateTimeString(manualForm.workDate, manualForm.checkInAt);
      const checkOut = manualForm.hasCheckOut
        ? toLocalDateTimeString(manualForm.workDate, manualForm.checkOutAt)
        : null;
      await api.post(`/kitchen-employees/${selectedId}/attendance/manual`, {
        workDate: `${manualForm.workDate}T00:00:00`,
        checkInAt: checkIn,
        checkOutAt: checkOut,
        hourCoefficient: Number(manualForm.hourCoefficient || 1),
        comment: manualForm.comment,
      });
      setShowManualForm(false);
      setManualForm({ workDate: getLocalDateInputValue(), checkInAt: '09:00', checkOutAt: '18:00', hasCheckOut: true, hourCoefficient: '1', comment: '' });
      await loadDetails(selectedId);
    } catch (e: any) {
      setMessage(e?.response?.data?.message || 'Не вдалося додати запис.');
    }
  };

  const startEditAttendanceRow = (entry: EmployeeAttendanceEntry) => {
    const ci = new Date(entry.checkInAt);
    const co = entry.checkOutAt ? new Date(entry.checkOutAt) : null;
    const pad = (n: number) => String(n).padStart(2, '0');
    setEditingAttendanceId(entry.id);
    setEditingAttendanceRow({
      checkInDate: getLocalDateInputValue(ci),
      checkInTime: `${pad(ci.getHours())}:${pad(ci.getMinutes())}`,
      checkOutDate: co ? getLocalDateInputValue(co) : getLocalDateInputValue(ci),
      checkOutTime: co ? `${pad(co.getHours())}:${pad(co.getMinutes())}` : '',
      hasCheckOut: !!co,
      hourCoefficient: String(entry.hourCoefficient),
      comment: entry.comment || '',
    });
  };

  const saveEditAttendanceRow = async () => {
    if (editingAttendanceId == null) return;
    try {
      const r = editingAttendanceRow;
      const checkIn = toLocalDateTimeString(r.checkInDate, r.checkInTime);
      const checkOut = r.hasCheckOut && r.checkOutTime
        ? toLocalDateTimeString(r.checkOutDate, r.checkOutTime)
        : null;
      await api.put(`/kitchen-employees/attendance/${editingAttendanceId}`, {
        checkInAt: checkIn,
        checkOutAt: checkOut,
        hourCoefficient: Number(r.hourCoefficient || 1),
        comment: r.comment,
      });
      setEditingAttendanceId(null);
      if (selectedId) await loadDetails(selectedId);
    } catch (e: any) {
      setMessage(e?.response?.data?.message || 'Не вдалося зберегти зміни.');
    }
  };

  const deleteAttendanceEntry = async (entryId: number) => {
    if (!window.confirm('Видалити цей запис табелю?')) return;
    try {
      await api.delete(`/kitchen-employees/attendance/${entryId}`);
      if (selectedId) await loadDetails(selectedId);
    } catch {
      setMessage('Не вдалося видалити запис.');
    }
  };

  const applyBulkCoefficient = async () => {
    if (!bulkSelectedIds.length) return;
    await api.put('/kitchen-employees/attendance/daily-coefficient', {
      workDate: `${bulkDate}T00:00:00`,
      appUserIds: bulkSelectedIds,
      hourCoefficient: Number(bulkCoeff || 1),
      comment: bulkComment,
    });
    if (selectedId) await loadDetails(selectedId);
    setMessage('Коефіцієнт застосовано.');
  };

  const createDepartment = async () => {
    await api.post('/departments', {
      name: newDepartment.name,
      description: newDepartment.description,
      parentId: newDepartment.parentId ? Number(newDepartment.parentId) : null,
      salaryFundLimit: Number(newDepartment.salaryFundLimit || 0),
      salaryFundPercent: Number(newDepartment.salaryFundPercent || 0),
    });
    setNewDepartment({ name: '', description: '', parentId: '', salaryFundLimit: '0', salaryFundPercent: '0' });
    await loadDepartments();
    await loadEmployees(selectedId);
  };

  const startEditDepartment = (dept: Department) => {
    setEditingDepartmentId(dept.id);
    setEditingDepartment({
      name: dept.name,
      description: dept.description,
      parentId: dept.parentId ? String(dept.parentId) : '',
      salaryFundLimit: String(dept.salaryFundLimit ?? 0),
      salaryFundPercent: String(dept.salaryFundPercent ?? 0),
    });
  };

  const saveDepartment = async () => {
    if (editingDepartmentId === null) return;
    setMessage('');
    try {
      await api.put(`/departments/${editingDepartmentId}`, {
        name: editingDepartment.name,
        description: editingDepartment.description,
        parentId: editingDepartment.parentId ? Number(editingDepartment.parentId) : null,
        salaryFundLimit: Number(editingDepartment.salaryFundLimit || 0),
        salaryFundPercent: Number(editingDepartment.salaryFundPercent || 0),
      });
      setEditingDepartmentId(null);
      setEditingDepartment({ name: '', description: '', parentId: '', salaryFundLimit: '0', salaryFundPercent: '0' });
      await loadDepartments();
      setMessage('Підрозділ оновлено.');
    } catch (e: any) {
      const responseMessage = e?.response?.data?.message;
      const validationMessage = Array.isArray(e?.response?.data?.errors)
        ? e.response.data.errors.join('; ')
        : null;
      setMessage(responseMessage || validationMessage || e?.message || 'Не вдалося зберегти підрозділ.');
    }
  };

  const deleteDepartment = async (id: number) => {
    await api.delete(`/departments/${id}`);
    await loadDepartments();
    await loadEmployees(selectedId);
  };

  const createQuestion = async () => {
    await api.post('/evaluation-questions', {
      blockType: newQuestion.blockType,
      name: newQuestion.name,
      description: newQuestion.description,
      itemType: newQuestion.itemType,
      isActive: newQuestion.isActive,
      sortOrder: Number(newQuestion.sortOrder || 0),
    });
    setNewQuestion({
      blockType: 'qualification',
      name: '',
      description: '',
      itemType: 'positive',
      isActive: true,
      sortOrder: '0',
    });
    await loadQuestions();
  };

  const toggleQuestionActive = async (q: EvaluationQuestion) => {
    await api.put(`/evaluation-questions/${q.id}`, {
      blockType: q.blockType,
      name: q.name,
      description: q.description,
      itemType: q.itemType,
      isActive: !q.isActive,
      sortOrder: q.sortOrder,
    });
    await loadQuestions();
  };

  const deleteQuestion = async (id: number) => {
    await api.delete(`/evaluation-questions/${id}`);
    await loadQuestions();
  };

  const saveRevenue = async () => {
    const [yearText, monthText] = (revenueForm.period || '').split('-');
    const year = Number(yearText || 0);
    const month = Number(monthText || 0);
    if (!year || !month) {
      setMessage('Оберіть коректний місяць для виторгу.');
      return;
    }
    await api.put(`/monthly-revenues/${year}/${month}`, {
      year,
      month,
      amount: Number(revenueForm.amount || 0),
      isManual: !!revenueForm.isManual,
      includeAdditionalServices: !!revenueForm.includeAdditionalServices,
      notes: revenueForm.notes,
    });
    setEditingRevenueId(null);
    await loadRevenues();
    setMessage('Виторг збережено.');
  };

  const startEditRevenue = (r: MonthlyRevenue) => {
    setEditingRevenueId(r.id);
    setRevenueForm({
      period: `${r.year}-${String(r.month).padStart(2, '0')}`,
      amount: String(r.amount ?? 0),
      isManual: !!r.isManual,
      includeAdditionalServices: !!r.includeAdditionalServices,
      notes: r.notes ?? '',
    });
  };

  const cancelEditRevenue = () => {
    setEditingRevenueId(null);
    setRevenueForm({
      period: `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`,
      amount: '0',
      isManual: true,
      includeAdditionalServices: true,
      notes: '',
    });
  };

  const deleteRevenue = async (id: number) => {
    if (!window.confirm('Видалити запис виторгу за цей місяць?')) return;
    try {
      await api.delete(`/monthly-revenues/${id}`);
      await loadRevenues();
      setMessage('Запис виторгу видалено.');
    } catch (e: any) {
      setMessage(e?.response?.data?.message || e?.message || 'Не вдалося видалити запис виторгу.');
    }
  };

  const recalculateRevenue = async () => {
    const [yearText, monthText] = (revenueForm.period || '').split('-');
    const year = Number(yearText || 0);
    const month = Number(monthText || 0);
    const res = await api.get(`/monthly-revenues/calculate/${year}/${month}?includeAdditionalServices=${revenueForm.includeAdditionalServices ? 'true' : 'false'}`);
    setRevenueForm((p) => ({
      ...p,
      amount: String((res.data?.totalAmount ?? 0).toFixed(2)),
      isManual: false,
    }));
  };

  const tabButton = (key: TabKey, label: string) => (
    <button
      type="button"
      className="hr-tab-button"
      aria-pressed={tab === key}
      onClick={() => setTab(key)}
      style={{
        padding: '8px 12px',
        borderRadius: 'var(--crm-radius-sm)',
        cursor: 'pointer',
        background: tab === key ? 'var(--crm-primary)' : 'var(--crm-surface-alt)',
        color: tab === key ? 'var(--crm-primary-contrast)' : 'var(--crm-text)',
        border: `1px solid ${tab === key ? 'var(--crm-primary)' : 'var(--crm-border)'}`,
      }}
    >
      {label}
    </button>
  );

  const saveGridCell = async (appUserId: string, workDate: string, entryId: number | null, checkInAt: string, checkOutAt: string, checkOutNextDay: boolean, hourCoefficient: string, comment: string): Promise<boolean> => {
    if (!checkInAt || !checkOutAt) {
      setMessage('Вкажіть час приходу і виходу.');
      return false;
    }
    setGridSavingCell(`${appUserId}|${workDate}`);
    try {
      const workDateOnly = workDate.split('T')[0];
      const checkOutDateOnly = checkOutNextDay ? getNextLocalDateInputValue(workDateOnly) : workDateOnly;
      const payload = {
        workDate: workDateOnly,
        checkInTime: checkInAt.includes('T') ? checkInAt.split('T')[1].slice(0, 5) : checkInAt,
        checkOutTime: checkOutAt.includes('T') ? checkOutAt.split('T')[1].slice(0, 5) : checkOutAt,
        checkOutDate: checkOutDateOnly,
        hourCoefficient: Number(hourCoefficient || 1),
        comment,
        entryId,
      };
      await api.put(`/kitchen-employees/attendance-grid/${appUserId}`, payload);
      await loadAttendanceGrid();
      return true;
    } catch (e: any) {
      console.error('HR attendance grid save failed', {
        responseStatus: e?.response?.status,
      });
      setMessage(e?.response?.data?.message || 'Не вдалося зберегти табель.');
      return false;
    } finally {
      setGridSavingCell(null);
    }
  };

  const handleImportWorkbook = async (file: File) => {
    setImportingWorkbook(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/import/kitchen-hr-evaluation/preview', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const preview = res.data || {};
      setImportPreview({
        employees: Number(preview.employees || 0),
        questions: Number(preview.questions || 0),
        salaryRules: Number(preview.salaryRules || 0),
        preview: preview.preview || preview.data || undefined,
      });
      setImportFile(file);
      setMessage(`Підготовлено до імпорту: працівники ${Number(preview.employees || 0)}, питання ${Number(preview.questions || 0)}, правила ${Number(preview.salaryRules || 0)}.`);
    } finally {
      setImportingWorkbook(false);
      if (importFileRef.current) importFileRef.current.value = '';
    }
  };

  const confirmWorkbookImport = async () => {
    if (!importFile || !importPreview) return;
    setImportingWorkbook(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      await api.post('/import/kitchen-hr-evaluation/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      await loadEmployees(selectedId);
      await loadQuestions();
      setMessage(`Імпорт завершено: працівники ${importPreview.employees}, питання ${importPreview.questions}, правила ${importPreview.salaryRules}.`);
      setImportPreview(null);
      setImportFile(null);
    } finally {
      setImportingWorkbook(false);
    }
  };

  const openGridCellEditor = (
    row: HRAttendanceGrid['employees'][number],
    cell: HRAttendanceGrid['employees'][number]['cells'][number],
    entry: HRAttendanceGrid['employees'][number]['cells'][number]['entries'][number] | null = cell.entries[cell.entries.length - 1] ?? null
  ) => {
    const draft = {
      checkInAt: entry ? new Date(entry.checkInAt).toTimeString().slice(0, 5) : '',
      checkOutAt: entry?.checkOutAt ? new Date(entry.checkOutAt).toTimeString().slice(0, 5) : '',
      checkOutNextDay: !!entry?.checkOutAt && entry.checkInAt ? new Date(entry.checkOutAt).toDateString() !== new Date(entry.checkInAt).toDateString() : false,
      hourCoefficient: String(entry?.hourCoefficient ?? 1),
      comment: entry?.comment || '',
    };
    setEditingGridCell({
      appUserId: row.appUserId,
      workDate: cell.workDate,
      fullName: row.fullName,
      departmentName: row.departmentName || '—',
      entries: cell.entries,
      entryId: entry?.entryId ?? null,
      checkInAt: draft.checkInAt,
      checkOutAt: draft.checkOutAt,
      checkOutNextDay: draft.checkOutNextDay,
      hourCoefficient: draft.hourCoefficient,
      comment: draft.comment,
    });
  };

  const selectGridEditorEntry = (entry: HRAttendanceGrid['employees'][number]['cells'][number]['entries'][number] | null) => {
    setEditingGridCell((current) => current ? {
      ...current,
      entryId: entry?.entryId ?? null,
      checkInAt: entry ? new Date(entry.checkInAt).toTimeString().slice(0, 5) : '',
      checkOutAt: entry?.checkOutAt ? new Date(entry.checkOutAt).toTimeString().slice(0, 5) : '',
      checkOutNextDay: !!entry?.checkOutAt && !!entry.checkInAt &&
        new Date(entry.checkOutAt).toDateString() !== new Date(entry.checkInAt).toDateString(),
      hourCoefficient: String(entry?.hourCoefficient ?? 1),
      comment: entry?.comment || '',
    } : current);
  };

  const visibleGridEmployees = useMemo(() => {
    const query = gridSearch.trim().toLowerCase();
    const rows = attendanceGrid?.employees ?? [];
    const filtered = query
      ? rows.filter((row) =>
          row.fullName.toLowerCase().includes(query) ||
          (row.departmentName || '').toLowerCase().includes(query)
        )
      : rows;
    const sorted = [...filtered];
    sorted.sort((a, b) => {
      if (gridSort === 'name-asc') return a.fullName.localeCompare(b.fullName, 'uk');
      if (gridSort === 'name-desc') return b.fullName.localeCompare(a.fullName, 'uk');
      const aHours = a.cells.reduce((sum, cell) => sum + getCellWeightedHours(cell), 0);
      const bHours = b.cells.reduce((sum, cell) => sum + getCellWeightedHours(cell), 0);
      return gridSort === 'hours-desc' ? bHours - aHours : aHours - bHours;
    });
    return sorted;
  }, [attendanceGrid, gridSearch, gridSort]);

  const totalGridHoursByEmployee = useMemo(() => {
    const map = new Map<string, number>();
    for (const row of attendanceGrid?.employees ?? []) {
      map.set(
        row.appUserId,
        row.cells.reduce((sum, cell) => sum + getCellWeightedHours(cell), 0)
      );
    }
    return map;
  }, [attendanceGrid]);

  const monthlyAttendanceTotals = useMemo(() => {
    return visibleGridEmployees.reduce(
      (totals, row) => row.cells.reduce((rowTotals, cell) => {
        const hours = getCellHours(cell) ?? 0;
        const roundedHours = Number(hours.toFixed(2));
        rowTotals.actual += roundedHours;
        rowTotals.weighted += getCellWeightedHours(cell);
        return rowTotals;
      }, totals),
      { actual: 0, weighted: 0 }
    );
  }, [visibleGridEmployees]);

  const exportMonthlyAttendanceExcel = () => {
    if (visibleGridEmployees.length === 0) {
      window.alert('Немає даних для експорту.');
      return;
    }

    const rows = visibleGridEmployees.map((row) => ({
      'ПІБ працівника': row.fullName,
      'Відпрацьовані години': Number(
        row.cells.reduce((sum, cell) => sum + Number((getCellHours(cell) ?? 0).toFixed(2)), 0).toFixed(2)
      ),
    }));
    const worksheet = XLSX.utils.json_to_sheet(rows);
    worksheet['!cols'] = [{ wch: 32 }, { wch: 22 }];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Табель');
    XLSX.writeFile(workbook, `Табель_годин_${gridMonth}.xlsx`);
  };

  const attendanceDayStyles = useMemo(() => {
    const days = attendanceGrid?.days ?? [];
    return days.map((day, index) => {
      const date = new Date(day);
      const weekend = [0, 6].includes(date.getDay());
      return {
        background: weekend ? 'var(--crm-warning-bg)' : index % 2 === 0 ? 'var(--crm-surface)' : 'var(--crm-surface-alt)',
        borderColor: weekend ? 'var(--crm-warning)' : 'var(--crm-border)',
        textColor: weekend ? 'var(--crm-warning)' : 'var(--crm-text)',
      };
    });
  }, [attendanceGrid]);

  const printHtml = (html: string) => {
    printHtmlInHiddenFrame(html, { minimumDelayMs: 100 });
  };

  const buildAttendancePrintHtml = (format: 'compact' | 'landscape' | 'expanded' | 'expanded-no-hours') => {
    const rows = visibleGridEmployees;
    const days = attendanceGrid?.days ?? [];
    const styles = '@page { size: landscape; margin: 10mm; }';
    const isExpanded = format === 'expanded' || format === 'expanded-no-hours';
    const isExpandedNoHours = format === 'expanded-no-hours';
    const renderCellValue = (cell: HRAttendanceGrid['employees'][number]['cells'][number]) => {
      if (!cell.hasEntry) return '—';
      if (!isExpanded) return getCellWeightedHours(cell).toFixed(1);
      return cell.entries.map((entry) => {
        const checkIn = new Date(entry.checkInAt).toTimeString().slice(0, 5);
        const checkOut = entry.checkOutAt ? new Date(entry.checkOutAt).toTimeString().slice(0, 5) : 'відкрита';
        if (isExpandedNoHours) return `${checkIn} / ${checkOut}`;
        const actualHours = getAttendanceEntryHours(entry);
        const weightedHours = actualHours * Number(entry.hourCoefficient ?? 1);
        return `${checkIn} / ${checkOut} / ${actualHours.toFixed(1)} × ${Number(entry.hourCoefficient ?? 1).toFixed(2)} = ${weightedHours.toFixed(1)}`;
      }).join('; ');
    };
    const dayHeaders = isExpandedNoHours
      ? days.map((day) => `<th>${new Date(day).getDate()}<div class="muted">${new Date(day).toLocaleDateString('uk-UA', { weekday: 'short' })}</div></th>`).join('')
      : isExpanded
      ? days.map((day) => `<th>${new Date(day).getDate()}<div class="muted">${new Date(day).toLocaleDateString('uk-UA', { weekday: 'short' })}</div></th>`).join('')
      : days.map((day) => `<th>${new Date(day).getDate()}</th>`).join('');
    const html = `
      <html>
        <head>
          <title>Табель</title>
          <style>
            ${styles}
            body { font-family: Arial, sans-serif; color: #111; background: #f3f4f6; }
            .sheet { background: #fff; border-radius: 12px; box-shadow: 0 8px 24px rgba(0,0,0,.12); padding: 14px; }
            table { border-collapse: collapse; width: 100%; font-size: 8px; }
            th, td { border: 1px solid #999; padding: 1px 0; text-align: center; }
            th:first-child, td:first-child { text-align: left; white-space: nowrap; }
            .muted { color: #666; font-size: 9px; }
            .total { font-weight: 700; }
            .expanded { font-size: 7px; line-height: 1.1; }
          </style>
        </head>
        <body>
          <div class="sheet">
          <h3>Табель ${gridMonth}</h3>
          <div class="muted">${gridDepartmentId ? 'Підрозділ: ' + (departments.find((d) => String(d.id) === gridDepartmentId)?.name || '') : 'Усі підрозділи'}</div>
          ${isExpandedNoHours ? '<div class="muted">Розгорнутий друк: прихід / вихід без підрахунку годин</div>' : (isExpanded ? '<div class="muted">Розгорнутий друк: прихід / вихід / години</div>' : '')}
          <table>
            <thead>
              <tr>
                <th>Працівник</th>
                ${dayHeaders}
                <th>Зараховано год</th>
              </tr>
            </thead>
            <tbody>
              ${rows.map((row) => `
                <tr>
                  <td>${row.fullName}</td>
                  ${row.cells.map((cell) => `<td class="${isExpanded ? 'expanded' : ''}">${renderCellValue(cell)}</td>`).join('')}
                  ${isExpandedNoHours ? '' : `<td class="total">${row.cells.reduce((sum, cell) => sum + getCellWeightedHours(cell), 0).toFixed(1)}</td>`}
                </tr>
              `).join('')}
            </tbody>
          </table>
          </div>
        </body>
      </html>
    `;
    return html;
  };

  const printCurrentEmployeeDetails = () => {
    if (!selectedSummary && !details) return;
    const employee = details || selectedSummary;
    if (!employee) return;
    const attendance = (details?.attendance || []).slice().sort((a, b) => b.workDate.localeCompare(a.workDate));
    const totalHours = attendance.reduce((sum, item) => sum + Number(item.weightedHours || 0), 0);
    const actualHours = attendance.reduce((sum, item) => sum + Number(item.hoursWorked || 0), 0);
    const hourlyRate = employee.useFixedRateSalary
      ? (Number(employee.fixedMonthlyRate || 0) + (Number(selectedRevenue?.amount || 0) * Number(employee.revenuePercent || 0)) / 100) / Math.max(1, totalHours)
      : null;
    const salaryAmount = employee.useFixedRateSalary
      ? Number(employee.fixedMonthlyRate || 0) + (Number(selectedRevenue?.amount || 0) * Number(employee.revenuePercent || 0)) / 100
      : totalHours * Number(employee.summaryCoefficient || 0);
    const html = `
      <html>
        <head>
          <title>Працівник</title>
          <style>
            @page { size: A4 portrait; margin: 10mm; }
            body { font-family: Arial, sans-serif; color: #111; font-size: 11px; }
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid #999; padding: 4px; text-align: left; }
          </style>
        </head>
        <body>
          <div class="sheet">
          <h3>${employee.fullName}</h3>
          <div>${employee.position}</div>
          <div>Підрозділ: ${employee.departmentName || '—'}</div>
          <div>Підсумковий коефіцієнт: ${employee.summaryCoefficient.toFixed(4)}</div>
          <div>Фактично відпрацьовано: ${actualHours.toFixed(2)} год</div>
          <div>Зараховано з урахуванням коефіцієнтів: ${totalHours.toFixed(2)} год</div>
          <div>Сума зарплати: ${salaryAmount.toFixed(2)} ₴</div>
          <div>Оплата: ${employee.useFixedRateSalary ? 'ставка + % від виторгу' : 'погодинно'}</div>
          ${hourlyRate !== null ? `<div>Погодинна ставка: ${hourlyRate.toFixed(2)} ₴/год</div>` : ''}
          <h4>Оцінювання</h4>
          <table>
            <tbody>
              ${[
                ['Кваліфікація', employee.qualification],
                ['Продуктивність', employee.productivity],
                ['Відповідальність', employee.responsibility],
                ['Базовий коефіцієнт', employee.baseCoefficient],
                ['Фінальний коефіцієнт', employee.finalCoefficient],
                ['Підсумковий коефіцієнт', employee.summaryCoefficient],
              ].map(([label, value]) => `<tr><td>${label}</td><td>${Number(value || 0).toFixed(4)}</td></tr>`).join('')}
            </tbody>
          </table>
          <h4>Табель</h4>
          <table>
            <thead>
              <tr><th>Дата</th><th>Прихід</th><th>Вихід</th><th>Факт, год</th><th>Коеф.</th><th>Зараховано, год</th><th>Коментар</th></tr>
            </thead>
            <tbody>
              ${attendance.map((a) => `
                <tr>
                  <td>${a.workDate.split('T')[0]}</td>
                  <td>${a.checkInAt ? new Date(a.checkInAt).toTimeString().slice(0, 5) : '—'}</td>
                  <td>${a.checkOutAt ? new Date(a.checkOutAt).toTimeString().slice(0, 5) : '—'}</td>
                  <td>${a.hoursWorked.toFixed(2)}</td>
                  <td>${a.hourCoefficient.toFixed(2)}</td>
                  <td>${a.weightedHours.toFixed(2)}</td>
                  <td>${a.comment || '—'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          </div>
        </body>
      </html>
    `;
    printHtml(html);
  };

  const printAttendanceByMode = () => {
    printHtml(buildAttendancePrintHtml('compact'));
  };

  const printAttendanceExpandedNoHours = () => {
    printHtml(buildAttendancePrintHtml('expanded-no-hours'));
  };

  return (
    <div className="page-shell page-shell--wide hr-page" style={{ width: '100%', maxWidth: 'none', boxSizing: 'border-box' }}>
      <h2 style={{ borderBottom: '2px solid var(--crm-accent)', paddingBottom: 8 }}>
        HR: персонал, оцінка, підрозділи, виторг
      </h2>
      <p>Спільний кадровий розділ усієї компанії. Операційна група не фільтрує працівників або кадрові підсумки.</p>
      {message && <div role="status" style={{ ...panelStyle, marginTop: 8, padding: 10 }}>{message}</div>}
      {loadError && <div role="alert" style={{ ...panelStyle, marginTop: 8, padding: 10 }}>{loadError}</div>}

      <div className="hr-page__tabs" style={{ display: 'flex', gap: 8, marginTop: 12, marginBottom: 14 }}>
        {tabButton('employees', 'Персонал')}
        {tabButton('attendance', 'Табель')}
        {tabButton('departments', 'Підрозділи')}
        {tabButton('questions', 'Питання оцінки')}
        {tabButton('revenue', 'Виторг')}
        <button
          type="button"
          className="hr-tab-button"
          onClick={() => importFileRef.current?.click()}
          disabled={importingWorkbook}
          style={{ ...buttonStyle('info'), cursor: 'pointer' }}
        >
          {importingWorkbook ? 'Імпорт…' : 'Імпорт Excel'}
        </button>
        <button
          type="button"
          className="hr-tab-button"
          onClick={() => { void downloadHrTemplate().catch(error => setMessage(extractApiErrorMessage(error, 'Не вдалося завантажити шаблон.'))); }}
          style={{ ...buttonStyle('muted'), cursor: 'pointer' }}
        >
          Завантажити шаблон
        </button>
        <input
          ref={importFileRef}
          type="file"
          accept=".xlsx,.xlsm,.xls"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleImportWorkbook(file);
          }}
          style={{ display: 'none' }}
        />
      </div>

      {importPreview && (
        <div style={{ ...panelStyle, marginBottom: 14, padding: 12, background: 'var(--crm-info-bg)', borderColor: 'var(--crm-info)' }}>
          <div style={{ marginBottom: 8, fontWeight: 700 }}>Підготовлено до імпорту</div>
          <div style={{ fontSize: 13, color: 'var(--crm-info)' }}>
            Працівники: {importPreview.employees} • Питання: {importPreview.questions} • Правила ЗП: {importPreview.salaryRules}
          </div>
          {importPreview.preview && (
            <div className="kitchen-employee-import-preview" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginTop: 10 }}>
              <div style={{ ...panelStyle, maxHeight: 220, overflow: 'auto', padding: 8 }}>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>Працівники</div>
                {importPreview.preview.employees.map((item) => (
                  <div key={`${item.fullName}-${item.position}`} style={{ padding: '6px 0', borderBottom: '1px solid var(--crm-border)' }}>
                    <div style={{ fontWeight: 600 }}>{item.fullName}</div>
                    <div style={{ fontSize: 12, color: 'var(--crm-text-muted)' }}>{item.position} • {item.monthsWorked} міс • коєф. {item.positionCoefficient}</div>
                  </div>
                ))}
              </div>
              <div style={{ ...panelStyle, maxHeight: 220, overflow: 'auto', padding: 8 }}>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>Питання</div>
                {importPreview.preview.questions.map((item) => (
                  <div key={`${item.blockType}-${item.sortOrder}-${item.name}`} style={{ padding: '6px 0', borderBottom: '1px solid var(--crm-border)' }}>
                    <div style={{ fontWeight: 600 }}>{item.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--crm-text-muted)' }}>{item.blockType} • {item.itemType} • №{item.sortOrder}</div>
                  </div>
                ))}
              </div>
              <div style={{ ...panelStyle, maxHeight: 220, overflow: 'auto', padding: 8 }}>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>Правила ЗП</div>
                {importPreview.preview.salaryRules.map((item) => (
                  <div key={item.position} style={{ padding: '6px 0', borderBottom: '1px solid var(--crm-border)' }}>
                    <div style={{ fontWeight: 600 }}>{item.position}</div>
                    <div style={{ fontSize: 12, color: 'var(--crm-text-muted)' }}>коєф. {item.positionCoefficient}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="hr-action-row" style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button type="button" onClick={confirmWorkbookImport} disabled={importingWorkbook} style={{ ...buttonStyle('success'), cursor: 'pointer' }}>
              {importingWorkbook ? 'Імпорт…' : 'Підтвердити імпорт'}
            </button>
            <button
              type="button"
              onClick={() => { setImportPreview(null); setImportFile(null); }}
              style={{ ...buttonStyle('muted'), cursor: 'pointer' }}
            >
              Скасувати
            </button>
          </div>
        </div>
      )}

      {tab === 'employees' && (
        <div className="responsive-split-layout hr-personnel-layout" style={{ marginTop: 12 }}>
          <div>
            <div style={{ marginBottom: 10, color: 'var(--crm-text-muted)', fontSize: 13 }}>
              Список формується з усіх користувачів системи.
            </div>
            <button className="hr-tab-button" onClick={startManualCreateEmployee} style={{ ...buttonStyle('success'), marginBottom: 10 }}>
              + Додати працівника вручну
            </button>
            <div className="hr-employee-list" role="list">
              {employees.map((e) => (
                <div
                  role="listitem"
                  key={e.id}
                  className={`hr-employee-card${selectedId === e.id ? ' hr-employee-card--selected' : ''}`}
                >
                  <button
                    type="button"
                    className="hr-employee-card__select"
                    aria-pressed={selectedId === e.id}
                    onClick={() => { setSelectedId(e.id); loadDetails(e.id); }}
                  >
                    <span className="hr-employee-card__identity">
                      <strong>{e.fullName || 'Без імені'}</strong>
                      <span>{e.position || 'Посаду не вказано'}{e.departmentName ? ` · ${e.departmentName}` : ''}</span>
                    </span>
                    <span className="hr-employee-card__meta">
                      <span className={`hr-employee-status${e.isActive ? '' : ' hr-employee-status--inactive'}`}>
                        {e.isActive ? 'Активний' : 'Звільнений'}
                      </span>
                      <span className="hr-employee-card__coefficient" aria-label={`Коефіцієнт ${e.summaryCoefficient.toFixed(4)}`}>
                        ×{e.summaryCoefficient.toFixed(2)}
                      </span>
                    </span>
                  </button>
                  <div className="hr-employee-card__attendance">
                    <button
                      type="button"
                      className="hr-attendance-action hr-attendance-action--in"
                      aria-label={`Відмітити прихід: ${e.fullName}`}
                      title={`Відмітити прихід: ${e.fullName}`}
                      disabled={!e.useAttendance}
                      onClick={() => { void managerCheckIn(e.id); }}
                    >
                      +
                    </button>
                    <button
                      type="button"
                      className="hr-attendance-action hr-attendance-action--out"
                      aria-label={`Відмітити вихід: ${e.fullName}`}
                      title={`Відмітити вихід: ${e.fullName}`}
                      disabled={!e.useAttendance}
                      onClick={() => { void managerCheckOut(e.id); }}
                    >
                      −
                    </button>
                  </div>
                </div>
              ))}
              {!loading && !loadError && employees.length === 0 && (
                <div className="hr-employee-list__empty">Працівників немає</div>
              )}
            </div>

            <details className="hr-collapsible" style={{ marginTop: 12 }}>
              <summary>Масовий коефіцієнт дня</summary>
              <div className="hr-collapsible__content">
              <div className="hr-bulk-coefficient__fields">
                <label>Дата коефіцієнта
                  <input type="date" value={bulkDate} onChange={(e) => setBulkDate(e.target.value)} />
                </label>
                <label>Коефіцієнт дня
                  <input type="number" step="0.01" value={bulkCoeff} onChange={(e) => setBulkCoeff(e.target.value)} placeholder="Коеф." />
                </label>
              </div>
              <input style={{ width: '100%', marginBottom: 8 }} value={bulkComment} onChange={(e) => setBulkComment(e.target.value)} placeholder="Коментар (необов'язково)" />
              <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)', padding: 8 }}>
                {employees.map((e) => (
                  <label key={`bulk-${e.id}`} style={{ display: 'block', marginBottom: 4 }}>
                    <input
                      type="checkbox"
                      checked={bulkSelectedIds.includes(e.id)}
                      onChange={(ev) => setBulkSelectedIds((prev) => ev.target.checked ? [...prev, e.id] : prev.filter((x) => x !== e.id))}
                    />
                    {' '}
                    {e.fullName}
                  </label>
                ))}
              </div>
              <button onClick={applyBulkCoefficient} style={{ ...buttonStyle('info'), marginTop: 8 }}>
                Застосувати
              </button>
              </div>
            </details>
          </div>

          <div>
            {(details || selectedSummary || manualCreateMode) && (
              <div style={{ ...panelStyle, padding: 16, marginBottom: 16 }}>
                <h3 style={{ marginTop: 0 }}>{manualCreateMode ? 'Новий працівник' : (details?.fullName || selectedSummary?.fullName)}</h3>
                {manualCreateMode && (
                  <div style={{ marginBottom: 10, padding: 10, background: 'var(--crm-warning-bg)', border: '1px solid var(--crm-warning)', borderRadius: 'var(--crm-radius-md)', color: 'var(--crm-warning)' }}>
                    Для нового працівника введіть посаду. Системні права налаштовуються окремо в обліковому записі.
                  </div>
                )}
                <div className="kitchen-employee-form-grid">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <input disabled={!editMode} value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} placeholder="ПІБ" />
                    <div style={{ fontSize: 11, color: 'var(--crm-text-muted)' }}>Повне ім’я працівника.</div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <input disabled={!editMode} value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} placeholder="Посада" />
                    <div style={{ fontSize: 11, color: 'var(--crm-text-muted)' }}>Кадрова посада для зарплатного правила; вона не змінює системні права.</div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <input
                      disabled
                      value={details ? `${details.monthsWorked} міс.` : 'Рахується від дати прийому'}
                      title="Час в колективі рахується автоматично від дати прийому на роботу"
                    />
                    <div style={{ fontSize: 11, color: 'var(--crm-text-muted)' }}>Час у колективі. Рахується автоматично від дати прийому.</div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <select disabled={!editMode} value={form.departmentId} onChange={(e) => setForm({ ...form, departmentId: e.target.value })}>
                      <option value="">Підрозділ: не задано</option>
                      {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                    <div style={{ fontSize: 11, color: 'var(--crm-text-muted)' }}>Підрозділ або команда, до якої прив’язаний працівник.</div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <input disabled={!editMode} type="number" step="0.01" value={form.positionCoefficient} onChange={(e) => setForm({ ...form, positionCoefficient: e.target.value })} placeholder="Коеф. посади" title="Множник посади у розрахунку ЗП" />
                    <div style={{ fontSize: 11, color: 'var(--crm-text-muted)' }}>Базовий множник зарплати. 1 = без змін, 0 = не задано.</div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <input disabled={!editMode} type="number" step="0.01" value={form.correction} onChange={(e) => setForm({ ...form, correction: e.target.value })} placeholder="Корекція (+/-)" title="Ручна поправка до коефіцієнта" />
                    <div style={{ fontSize: 11, color: 'var(--crm-text-muted)' }}>Додаткова ручна поправка до розрахунку. Може бути 0.</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <label><input disabled={!editMode} type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} /> Активний</label>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <label><input disabled={!editMode} type="checkbox" checked={form.useAttendance} onChange={(e) => setForm({ ...form, useAttendance: e.target.checked })} /> Табелювання по працівнику</label>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <label><input disabled={!editMode} type="checkbox" checked={form.useFixedRateSalary} onChange={(e) => setForm({ ...form, useFixedRateSalary: e.target.checked })} /> ЗП = ставка + % від виторгу</label>
                  </div>
                  <div />

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <input disabled={!editMode || !form.useFixedRateSalary} type="number" step="0.01" value={form.fixedMonthlyRate} onChange={(e) => setForm({ ...form, fixedMonthlyRate: e.target.value })} placeholder="Ставка, ₴" />
                    <div style={{ fontSize: 11, color: 'var(--crm-text-muted)' }}>Фіксована частина зарплати, якщо увімкнена ставка.</div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <input disabled={!editMode || !form.useFixedRateSalary} type="number" step="0.01" value={form.revenuePercent} onChange={(e) => setForm({ ...form, revenuePercent: e.target.value })} placeholder="% від виторгу" />
                    <div style={{ fontSize: 11, color: 'var(--crm-text-muted)' }}>Відсоток від місячного виторгу, якщо використовується змішана ЗП.</div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <input disabled value={`Поточний виторг: ${(selectedRevenue?.amount ?? 0).toFixed(2)} ₴`} />
                    <div style={{ fontSize: 11, color: 'var(--crm-text-muted)' }}>Сума виторгу за вибраний місяць.</div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <input disabled value={`Нарахування за період: ${revenueForm.period || 'не вибрано'}`} />
                    <div style={{ fontSize: 11, color: 'var(--crm-text-muted)' }}>
                      Точна сума формується в модулі «Зарплата» за календарний місяць, чинними правилами та коригуваннями; сума за весь час не використовується як прогноз.
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <input disabled value={`Фонд підрозділу: ${departmentSalaryFundPreview.toFixed(2)} ₴`} />
                    <div style={{ fontSize: 11, color: 'var(--crm-text-muted)' }}>
                      {selectedDepartmentSalaryFundPercent > 0
                        ? `Розрахунок: виторг × ${selectedDepartmentSalaryFundPercent.toFixed(2)}% для підрозділу.`
                        : 'Для цього підрозділу % фонду від виторгу ще не задано.'}
                    </div>
                  </div>
                </div>
                <input disabled={!editMode} style={{ width: '100%', marginTop: 8 }} value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} placeholder="Коментар" />
                <div className="hr-action-row" style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  {selectedId && !editMode && !manualCreateMode && (
                    <button onClick={() => setEditMode(true)} style={buttonStyle('info')}>
                      Редагувати працівника
                    </button>
                  )}
                  {selectedId && !editMode && !manualCreateMode && (
                    <button onClick={archiveEmployee} style={buttonStyle('warning')}>
                      Архівувати працівника
                    </button>
                  )}
                  {editMode && !manualCreateMode && (
                    <button disabled={saving} onClick={saveEmployee} style={buttonStyle('success')}>
                      Зберегти працівника
                    </button>
                  )}
                  {editMode && !manualCreateMode && (
                    <button onClick={() => { if (selectedId) loadDetails(selectedId); }} style={buttonStyle('muted')}>
                      Скасувати
                    </button>
                  )}
                  {manualCreateMode && editMode && (
                    <button onClick={async () => {
                      setSaving(true);
                      setMessage('');
                      try {
                        await api.post('/kitchen-employees', {
                          fullName: form.fullName,
                          position: form.position,
                          positionCoefficient: Number(form.positionCoefficient || 1),
                          correction: Number(form.correction || 0),
                          comment: form.comment,
                          isActive: form.isActive,
                          departmentId: form.departmentId ? Number(form.departmentId) : null,
                          useAttendance: form.useAttendance,
                          useFixedRateSalary: form.useFixedRateSalary,
                          fixedMonthlyRate: Number(form.fixedMonthlyRate || 0),
                          revenuePercent: Number(form.revenuePercent || 0),
                        });
                        setManualCreateMode(false);
                        setEditMode(false);
                        setForm(emptyForm);
                        await loadEmployees();
                        setMessage('Працівника створено.');
                      } catch (e: any) {
                        setMessage(e?.response?.data?.message || 'Не вдалося створити працівника.');
                      } finally {
                        setSaving(false);
                      }
                    }} style={buttonStyle('success')}>
                      Створити працівника
                    </button>
                  )}
                  {manualCreateMode && editMode && (
                    <button onClick={() => { setManualCreateMode(false); setEditMode(false); setForm(emptyForm); }} style={buttonStyle('muted')}>
                      Скасувати
                    </button>
                  )}
                  {(selectedSummary || details) && (
                    <button
                      type="button"
                      onClick={printCurrentEmployeeDetails}
                      style={buttonStyle('muted')}
                    >
                      Друк працівника
                    </button>
                  )}
                </div>
                {!form.useAttendance && <div style={{ marginTop: 8, color: 'var(--crm-text-muted)' }}>Табелювання для цього працівника вимкнено.</div>}
              </div>
            )}

            {details && (
              <>
                <details key={`coefficients-${selectedId}`} className="hr-collapsible" style={{ marginBottom: 16 }}>
                  <summary>Підсумкова таблиця коефіцієнтів</summary>
                  <div className="hr-collapsible__content">
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <tbody>
                      <tr><td style={{ padding: 6 }}>Кваліфікація</td><td style={{ padding: 6, textAlign: 'right' }}>{details.qualification.toFixed(4)}</td></tr>
                      <tr><td style={{ padding: 6 }}>Продуктивність</td><td style={{ padding: 6, textAlign: 'right' }}>{details.productivity.toFixed(4)}</td></tr>
                      <tr><td style={{ padding: 6 }}>Відповідальність</td><td style={{ padding: 6, textAlign: 'right' }}>{details.responsibility.toFixed(4)}</td></tr>
                      <tr><td style={{ padding: 6 }}>Базовий коефіцієнт</td><td style={{ padding: 6, textAlign: 'right' }}>{details.baseCoefficient.toFixed(4)}</td></tr>
                      <tr><td style={{ padding: 6 }}>Фінальний коефіцієнт</td><td style={{ padding: 6, textAlign: 'right' }}>{details.finalCoefficient.toFixed(4)}</td></tr>
                      <tr><td style={{ padding: 6, fontWeight: 700 }}>Підсумковий коефіцієнт</td><td style={{ padding: 6, textAlign: 'right', fontWeight: 700 }}>{details.summaryCoefficient.toFixed(4)}</td></tr>
                      <tr><td style={{ padding: 6 }}>Фактично відпрацьовано, год</td><td style={{ padding: 6, textAlign: 'right' }}>{details.totalHoursWorked.toFixed(2)}</td></tr>
                      <tr><td style={{ padding: 6, fontWeight: 700 }}>Зараховано годин з урахуванням коефіцієнтів</td><td style={{ padding: 6, textAlign: 'right', fontWeight: 700 }}>{details.totalWeightedHours.toFixed(2)}</td></tr>
                    </tbody>
                  </table>
                  </div>
                </details>

                <details key={`evaluation-${selectedId}`} className="hr-collapsible" style={{ marginBottom: 16 }}>
                  <summary>Оцінювання</summary>
                  <div className="hr-collapsible__content">
                  {scores.map((block) => (
                    <div key={block.blockType} style={{ ...panelStyle, marginBottom: 16, padding: 10 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <strong>{blockLabels[block.blockType] || block.blockType}</strong>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <select
                            disabled={!editMode}
                            value={questionPickerByBlock[block.blockType] || ''}
                            onChange={(e) => setQuestionPickerByBlock((prev) => ({ ...prev, [block.blockType]: e.target.value }))}
                          >
                            <option value="">Оберіть шаблон питання</option>
                            {questions
                              .filter((q) => q.isActive && q.blockType === block.blockType)
                              .map((q) => (
                                <option key={q.id} value={q.id}>{q.name}</option>
                              ))}
                          </select>
                          <button disabled={!editMode} onClick={() => addQuestionToBlock(block.blockType)} style={{ ...buttonStyle('info'), padding: '6px 10px', opacity: editMode ? 1 : 0.55 }}>+ З шаблону</button>
                          <button disabled={!editMode} onClick={() => addScoreItem(block.blockType, 'positive')} style={{ ...buttonStyle('success'), padding: '6px 10px', opacity: editMode ? 1 : 0.55 }}>+ Позитив</button>
                          <button disabled={!editMode} onClick={() => addScoreItem(block.blockType, 'penalty')} style={{ ...buttonStyle('danger'), padding: '6px 10px', opacity: editMode ? 1 : 0.55 }}>+ Штраф</button>
                        </div>
                      </div>
                      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
                        <thead>
                          <tr>
                            <th style={{ textAlign: 'left', padding: 6 }}>Назва</th>
                            <th style={{ textAlign: 'left', padding: 6 }}>Опис</th>
                            <th style={{ textAlign: 'left', padding: 6 }}>Тип</th>
                            <th style={{ textAlign: 'right', padding: 6 }}>Бал 0-10</th>
                            <th />
                          </tr>
                        </thead>
                        <tbody>
                          {block.items.map((item, idx) => (
                            <tr key={`${block.blockType}-${idx}`}>
                              <td style={{ padding: 6 }}><input disabled={!editMode} value={item.name} onChange={(e) => updateScoreItem(block.blockType, idx, { name: e.target.value })} /></td>
                              <td style={{ padding: 6 }}><input disabled={!editMode} value={item.description} onChange={(e) => updateScoreItem(block.blockType, idx, { description: e.target.value })} /></td>
                              <td style={{ padding: 6 }}>
                                <select disabled={!editMode} value={item.itemType} onChange={(e) => updateScoreItem(block.blockType, idx, { itemType: e.target.value })}>
                                  <option value="positive">positive</option>
                                  <option value="penalty">penalty</option>
                                </select>
                              </td>
                              <td style={{ padding: 6, textAlign: 'right' }}>
                                <input disabled={!editMode} type="number" min={0} max={10} step={0.1} value={item.score} onChange={(e) => updateScoreItem(block.blockType, idx, { score: Number(e.target.value) })} style={{ width: 90, textAlign: 'right' }} />
                              </td>
                              <td style={{ padding: 6 }}>
                                <button disabled={!editMode} onClick={() => removeScoreItem(block.blockType, idx)} style={{ ...buttonStyle('muted'), padding: '5px 8px', opacity: editMode ? 1 : 0.55 }}>✕</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                  {editMode && (
                    <button disabled={saving} onClick={saveScores} style={buttonStyle('info')}>
                      Зберегти оцінки
                    </button>
                  )}
                  </div>
                </details>

                <details key={`attendance-${selectedId}`} className="hr-collapsible" open>
                  <summary>Табель: окремі інтервали ({details.attendance.length})</summary>
                  <div className="hr-collapsible__content">
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                    {form.useAttendance && (
                      <button
                        type="button"
                        onClick={() => { setShowManualForm(!showManualForm); setEditingAttendanceId(null); }}
                        className="hr-tab-button"
                        style={{ ...buttonStyle('info'), padding: '7px 14px', cursor: 'pointer' }}
                      >
                        {showManualForm ? '✕ Закрити' : '+ Додати запис'}
                      </button>
                    )}
                    </div>
                  {!form.useAttendance && (
                    <div style={{ color: 'var(--crm-text-muted)' }}>Для цього працівника табелювання вимкнено.</div>
                  )}
                  {form.useAttendance && showManualForm && (
                    <details className="hr-collapsible kitchen-employee-manual-attendance" open>
                    <summary>Окремий інтервал</summary>
                    <div className="kitchen-employee-manual-attendance-grid" style={{ background: 'var(--crm-surface-alt)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', padding: 14, marginBottom: 14, display: 'grid', gridTemplateColumns: 'auto auto auto auto auto 1fr auto', gap: 8, alignItems: 'end' }}>
                      <div>
                        <div style={{ fontSize: 12, color: 'var(--crm-text-muted)', marginBottom: 3 }}>Дата</div>
                        <input type="date" value={manualForm.workDate} onChange={(e) => setManualForm((p) => ({ ...p, workDate: e.target.value }))} />
                      </div>
                      <div>
                        <div style={{ fontSize: 12, color: 'var(--crm-text-muted)', marginBottom: 3 }}>Прихід (час)</div>
                        <input type="time" value={manualForm.checkInAt} onChange={(e) => setManualForm((p) => ({ ...p, checkInAt: e.target.value }))} />
                      </div>
                      <div>
                        <div style={{ fontSize: 12, color: 'var(--crm-text-muted)', marginBottom: 3 }}>Вихід (час)</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <input type="checkbox" checked={manualForm.hasCheckOut} onChange={(e) => setManualForm((p) => ({ ...p, hasCheckOut: e.target.checked }))} id="mco" />
                          <label htmlFor="mco" style={{ fontSize: 12 }}>є</label>
                          <input type="time" disabled={!manualForm.hasCheckOut} value={manualForm.checkOutAt} onChange={(e) => setManualForm((p) => ({ ...p, checkOutAt: e.target.value }))} />
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: 12, color: 'var(--crm-text-muted)', marginBottom: 3 }}>Коеф.</div>
                        <input type="number" step="0.01" min="0.01" value={manualForm.hourCoefficient} onChange={(e) => setManualForm((p) => ({ ...p, hourCoefficient: e.target.value }))} style={{ width: 80 }} />
                      </div>
                      <div style={{ gridColumn: 'span 1' }}>
                        <div style={{ fontSize: 12, color: 'var(--crm-text-muted)', marginBottom: 3 }}>Коментар</div>
                        <input value={manualForm.comment} onChange={(e) => setManualForm((p) => ({ ...p, comment: e.target.value }))} placeholder="Коментар" style={{ width: 160 }} />
                      </div>
                      <div />
                      <button onClick={addManualAttendance} style={{ ...buttonStyle('success'), padding: '8px 16px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                        Зберегти
                      </button>
                    </div>
                    </details>
                  )}
                  {form.useAttendance && (
                    <div style={{ overflowX: 'auto' }}>
                      {editingAttendanceId !== null && (
                        <div role="status" className="hr-attendance-edit-label">Редагування інтервалу</div>
                      )}
                      <table className="hr-attendance-table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
                        <thead>
                          <tr>
                            <th style={{ textAlign: 'left', padding: 6 }}>Дата</th>
                            <th style={{ textAlign: 'left', padding: 6 }}>Прихід</th>
                            <th style={{ textAlign: 'left', padding: 6 }}>Вихід</th>
                            <th style={{ textAlign: 'right', padding: 6 }}>Годин</th>
                            <th style={{ textAlign: 'right', padding: 6 }}>Коеф.</th>
                            <th style={{ textAlign: 'right', padding: 6 }}>Зважені</th>
                            <th style={{ textAlign: 'left', padding: 6 }}>Коментар</th>
                            <th style={{ padding: 6 }} />
                          </tr>
                        </thead>
                        <tbody>
                          {details.attendance.map((a) => editingAttendanceId === a.id ? (
                            <tr key={a.id} style={{ background: 'var(--crm-warning-bg)' }}>
                              <td style={{ padding: 4 }}>
                                <input type="date" value={editingAttendanceRow.checkInDate} onChange={(e) => setEditingAttendanceRow((p) => ({ ...p, checkInDate: e.target.value, checkOutDate: p.checkOutDate || e.target.value }))} style={{ width: 130 }} />
                              </td>
                              <td style={{ padding: 4 }}>
                                <input type="time" value={editingAttendanceRow.checkInTime} onChange={(e) => setEditingAttendanceRow((p) => ({ ...p, checkInTime: e.target.value }))} />
                              </td>
                              <td style={{ padding: 4 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'nowrap' }}>
                                  <input type="checkbox" checked={editingAttendanceRow.hasCheckOut} onChange={(e) => setEditingAttendanceRow((p) => ({ ...p, hasCheckOut: e.target.checked }))} />
                                  {editingAttendanceRow.hasCheckOut && (
                                    <>
                                      <input type="date" value={editingAttendanceRow.checkOutDate} onChange={(e) => setEditingAttendanceRow((p) => ({ ...p, checkOutDate: e.target.value }))} style={{ width: 130 }} />
                                      <input type="time" value={editingAttendanceRow.checkOutTime} onChange={(e) => setEditingAttendanceRow((p) => ({ ...p, checkOutTime: e.target.value }))} />
                                    </>
                                  )}
                                </div>
                              </td>
                              <td style={{ padding: 4, textAlign: 'right' }}>—</td>
                              <td style={{ padding: 4 }}>
                                <input type="number" step="0.01" min="0.01" value={editingAttendanceRow.hourCoefficient} onChange={(e) => setEditingAttendanceRow((p) => ({ ...p, hourCoefficient: e.target.value }))} style={{ width: 80, textAlign: 'right' }} />
                              </td>
                              <td style={{ padding: 4, textAlign: 'right' }}>—</td>
                              <td style={{ padding: 4 }}>
                                <input value={editingAttendanceRow.comment} onChange={(e) => setEditingAttendanceRow((p) => ({ ...p, comment: e.target.value }))} style={{ width: '100%' }} />
                              </td>
                              <td style={{ padding: 4, whiteSpace: 'nowrap' }}>
                                <button onClick={saveEditAttendanceRow} style={{ ...buttonStyle('success'), padding: '5px 10px', marginRight: 4, cursor: 'pointer' }}>✓</button>
                                <button onClick={() => setEditingAttendanceId(null)} style={{ ...buttonStyle('muted'), padding: '5px 10px', cursor: 'pointer' }}>✕</button>
                              </td>
                            </tr>
                          ) : (
                            <tr key={a.id}>
                              <td style={{ padding: 6 }}>{new Date(a.workDate).toLocaleDateString('uk-UA')}</td>
                              <td style={{ padding: 6 }}>{new Date(a.checkInAt).toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' })}</td>
                              <td style={{ padding: 6 }}>{a.checkOutAt ? new Date(a.checkOutAt).toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                              <td style={{ padding: 6, textAlign: 'right' }}>{a.hoursWorked.toFixed(2)}</td>
                              <td style={{ padding: 6, textAlign: 'right' }}>{a.hourCoefficient.toFixed(2)}</td>
                              <td style={{ padding: 6, textAlign: 'right' }}>{a.weightedHours.toFixed(2)}</td>
                              <td style={{ padding: 6 }}>{a.comment || '—'}</td>
                              <td style={{ padding: 6, whiteSpace: 'nowrap' }}>
                                <button onClick={() => { startEditAttendanceRow(a); setShowManualForm(false); }} style={{ ...buttonStyle('info'), padding: '4px 9px', marginRight: 4, cursor: 'pointer' }} title="Редагувати">✎</button>
                                <button onClick={() => deleteAttendanceEntry(a.id)} style={{ ...buttonStyle('danger'), padding: '4px 9px', cursor: 'pointer' }} title="Видалити">🗑</button>
                              </td>
                            </tr>
                          ))}
                          {details.attendance.length === 0 && (
                            <tr><td colSpan={8} style={{ padding: 10, color: 'var(--crm-text-muted)', textAlign: 'center' }}>Табель порожній</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                </details>
              </>
            )}
            {details && <EmployeeDossierPanel key={details.appUserId} appUserId={details.appUserId} />}
          </div>
        </div>
      )}

      {tab === 'departments' && (
        <div className="kitchen-employees-split-layout" style={{ display: 'grid', gridTemplateColumns: '420px 1fr', gap: 16 }}>
          <div style={{ ...panelStyle, padding: 14 }}>
            <h3 style={{ marginTop: 0 }}>Додати підрозділ</h3>
            <input style={{ width: '100%', marginBottom: 4 }} value={newDepartment.name} onChange={(e) => setNewDepartment((p) => ({ ...p, name: e.target.value }))} placeholder="Назва" />
            <div style={{ fontSize: 11, color: 'var(--crm-text-muted)', marginBottom: 8 }}>Назва підрозділу: кухня, бар, зал тощо.</div>
            <input style={{ width: '100%', marginBottom: 4 }} value={newDepartment.description} onChange={(e) => setNewDepartment((p) => ({ ...p, description: e.target.value }))} placeholder="Опис" />
            <div style={{ fontSize: 11, color: 'var(--crm-text-muted)', marginBottom: 8 }}>Короткий внутрішній опис підрозділу.</div>
            <select style={{ width: '100%', marginBottom: 4 }} value={newDepartment.parentId} onChange={(e) => setNewDepartment((p) => ({ ...p, parentId: e.target.value }))}>
              <option value="">Без батьківського підрозділу</option>
              {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            <div style={{ fontSize: 11, color: 'var(--crm-text-muted)', marginBottom: 8 }}>Батьківський підрозділ використовується для структури та групування.</div>
            <input style={{ width: '100%', marginBottom: 4 }} type="number" step="0.01" value={newDepartment.salaryFundLimit} onChange={(e) => setNewDepartment((p) => ({ ...p, salaryFundLimit: e.target.value }))} placeholder="Фонд ЗП, ₴" />
            <div style={{ fontSize: 11, color: 'var(--crm-text-muted)', marginBottom: 8 }}>Фіксований ліміт фонду ЗП у гривнях.</div>
            <input style={{ width: '100%', marginBottom: 4 }} type="number" step="0.01" value={newDepartment.salaryFundPercent} onChange={(e) => setNewDepartment((p) => ({ ...p, salaryFundPercent: e.target.value }))} placeholder="% фонду від виторгу" />
            <div style={{ fontSize: 11, color: 'var(--crm-text-muted)', marginBottom: 8 }}>Виторг × цей відсоток = фонд ЗП підрозділу.</div>
            <button onClick={createDepartment} style={buttonStyle('success')}>
              Додати
            </button>
          </div>
          <div style={{ ...panelStyle, padding: 14 }}>
            <h3 style={{ marginTop: 0 }}>Підрозділи</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: 8 }}>Назва</th>
                  <th style={{ textAlign: 'left', padding: 8 }}>Батьківський</th>
                  <th style={{ textAlign: 'right', padding: 8 }}>Фонд ЗП</th>
                  <th style={{ textAlign: 'right', padding: 8 }}>% від виторгу</th>
                  <th style={{ textAlign: 'right', padding: 8 }}>Працівників</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {departments.map((d) => (
                  <tr key={d.id}>
                    <td style={{ padding: 8 }}>{d.name}</td>
                    <td style={{ padding: 8 }}>{d.parentName || '—'}</td>
                    <td style={{ padding: 8, textAlign: 'right' }}>{Number(d.salaryFundLimit ?? 0).toFixed(2)}</td>
                    <td style={{ padding: 8, textAlign: 'right' }}>{Number(d.salaryFundPercent ?? 0).toFixed(2)}%</td>
                    <td style={{ padding: 8, textAlign: 'right' }}>{d.employeeCount}</td>
                    <td style={{ padding: 8, textAlign: 'right' }}>
                      <button onClick={() => startEditDepartment(d)} style={{ ...buttonStyle('info'), padding: '6px 10px', marginRight: 6 }}>
                        Редагувати
                      </button>
                      <button onClick={() => deleteDepartment(d.id)} style={{ ...buttonStyle('danger'), padding: '6px 10px' }}>
                        Видалити
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {editingDepartmentId !== null && (
              <div style={{ ...panelStyle, marginTop: 12, padding: 12 }}>
                <h4 style={{ marginTop: 0 }}>Редагувати підрозділ</h4>
                <input style={{ width: '100%', marginBottom: 4 }} value={editingDepartment.name} onChange={(e) => setEditingDepartment((p) => ({ ...p, name: e.target.value }))} placeholder="Назва" />
                <div style={{ fontSize: 11, color: 'var(--crm-text-muted)', marginBottom: 8 }}>Назва підрозділу.</div>
                <input style={{ width: '100%', marginBottom: 4 }} value={editingDepartment.description} onChange={(e) => setEditingDepartment((p) => ({ ...p, description: e.target.value }))} placeholder="Опис" />
                <div style={{ fontSize: 11, color: 'var(--crm-text-muted)', marginBottom: 8 }}>Опис підрозділу.</div>
                <select style={{ width: '100%', marginBottom: 4 }} value={editingDepartment.parentId} onChange={(e) => setEditingDepartment((p) => ({ ...p, parentId: e.target.value }))}>
                  <option value="">Без батьківського підрозділу</option>
                  {departments.filter((d) => d.id !== editingDepartmentId).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
                <div style={{ fontSize: 11, color: 'var(--crm-text-muted)', marginBottom: 8 }}>Батьківський підрозділ.</div>
                <input style={{ width: '100%', marginBottom: 4 }} type="number" step="0.01" value={editingDepartment.salaryFundLimit} onChange={(e) => setEditingDepartment((p) => ({ ...p, salaryFundLimit: e.target.value }))} placeholder="Фонд ЗП, ₴" />
                <div style={{ fontSize: 11, color: 'var(--crm-text-muted)', marginBottom: 8 }}>Фіксований ліміт фонду ЗП.</div>
                <input style={{ width: '100%', marginBottom: 4 }} type="number" step="0.01" value={editingDepartment.salaryFundPercent} onChange={(e) => setEditingDepartment((p) => ({ ...p, salaryFundPercent: e.target.value }))} placeholder="% фонду від виторгу" />
                <div style={{ fontSize: 11, color: 'var(--crm-text-muted)', marginBottom: 8 }}>Відсоток від виторгу для розрахунку фонду.</div>
                <button onClick={saveDepartment} style={{ ...buttonStyle('success'), marginRight: 8 }}>Зберегти</button>
                <button onClick={() => setEditingDepartmentId(null)} style={buttonStyle('muted')}>Скасувати</button>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'questions' && (
        <div className="kitchen-employees-split-layout" style={{ display: 'grid', gridTemplateColumns: '420px 1fr', gap: 16 }}>
          <div style={{ ...panelStyle, padding: 14 }}>
            <h3 style={{ marginTop: 0 }}>Стандартизоване питання</h3>
            <select style={{ width: '100%', marginBottom: 8 }} value={newQuestion.blockType} onChange={(e) => setNewQuestion((p) => ({ ...p, blockType: e.target.value }))}>
              <option value="qualification">qualification</option>
              <option value="productivity">productivity</option>
              <option value="responsibility">responsibility</option>
            </select>
            <input style={{ width: '100%', marginBottom: 8 }} value={newQuestion.name} onChange={(e) => setNewQuestion((p) => ({ ...p, name: e.target.value }))} placeholder="Назва питання" />
            <input style={{ width: '100%', marginBottom: 8 }} value={newQuestion.description} onChange={(e) => setNewQuestion((p) => ({ ...p, description: e.target.value }))} placeholder="Опис" />
            <select style={{ width: '100%', marginBottom: 8 }} value={newQuestion.itemType} onChange={(e) => setNewQuestion((p) => ({ ...p, itemType: e.target.value }))}>
              <option value="positive">positive</option>
              <option value="penalty">penalty</option>
            </select>
            <input style={{ width: '100%', marginBottom: 8 }} type="number" value={newQuestion.sortOrder} onChange={(e) => setNewQuestion((p) => ({ ...p, sortOrder: e.target.value }))} placeholder="Сортування" />
            <label style={{ display: 'block', marginBottom: 10 }}>
              <input type="checkbox" checked={newQuestion.isActive} onChange={(e) => setNewQuestion((p) => ({ ...p, isActive: e.target.checked }))} /> Активне
            </label>
            <button onClick={createQuestion} style={buttonStyle('success')}>
              Додати питання
            </button>
          </div>

          <div style={{ ...panelStyle, padding: 14 }}>
            <h3 style={{ marginTop: 0 }}>Каталог питань</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: 8 }}>Блок</th>
                  <th style={{ textAlign: 'left', padding: 8 }}>Назва</th>
                  <th style={{ textAlign: 'left', padding: 8 }}>Тип</th>
                  <th style={{ textAlign: 'center', padding: 8 }}>Активне</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {questions.map((q) => (
                  <tr key={q.id}>
                    <td style={{ padding: 8 }}>{q.blockType}</td>
                    <td style={{ padding: 8 }}>
                      <strong>{q.name}</strong>
                      <div style={{ color: 'var(--crm-text-muted)', fontSize: 12 }}>{q.description}</div>
                    </td>
                    <td style={{ padding: 8 }}>{q.itemType}</td>
                    <td style={{ padding: 8, textAlign: 'center' }}>
                      <input type="checkbox" checked={q.isActive} onChange={() => toggleQuestionActive(q)} />
                    </td>
                    <td style={{ padding: 8, textAlign: 'right' }}>
                      <button onClick={() => deleteQuestion(q.id)} style={{ ...buttonStyle('danger'), padding: '6px 10px' }}>
                        Видалити
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'revenue' && (
        <div className="kitchen-employees-split-layout" style={{ display: 'grid', gridTemplateColumns: '420px 1fr', gap: 16 }}>
          <div style={{ ...panelStyle, padding: 14 }}>
            <h3 style={{ marginTop: 0 }}>Внесення виторгу за місяць</h3>
            {editingRevenueId !== null && (
              <div style={{ marginBottom: 8, padding: '8px 10px', borderRadius: 'var(--crm-radius-md)', background: 'var(--crm-success-bg)', color: 'var(--crm-success)', fontSize: 13 }}>
                Режим редагування запису з історії
              </div>
            )}
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <input
                type="checkbox"
                checked={revenueForm.isManual}
                onChange={(e) => setRevenueForm((p) => ({ ...p, isManual: e.target.checked }))}
              />
              Ручне введення виторгу
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <input
                type="checkbox"
                checked={revenueForm.includeAdditionalServices}
                onChange={(e) => setRevenueForm((p) => ({ ...p, includeAdditionalServices: e.target.checked }))}
              />
              Враховувати додаткові послуги
            </label>
            <input style={{ width: '100%', marginBottom: 8 }} type="month" value={revenueForm.period} onChange={(e) => setRevenueForm((p) => ({ ...p, period: e.target.value }))} />
            <div style={{ fontSize: 11, color: 'var(--crm-text-muted)', marginBottom: 8 }}>Оберіть календарний місяць, за який рахується виторг.</div>
            <input style={{ width: '100%', marginBottom: 4 }} type="number" step="0.01" value={revenueForm.amount} onChange={(e) => setRevenueForm((p) => ({ ...p, amount: e.target.value }))} placeholder="Сума виторгу, ₴" />
            <div style={{ fontSize: 11, color: 'var(--crm-text-muted)', marginBottom: 8 }}>
              {revenueForm.isManual
                ? 'Ручна сума виторгу за місяць.'
                : 'Сума буде перерахована з завершених замовлень за вибраний місяць.'}
            </div>
            <input style={{ width: '100%', marginBottom: 4 }} value={revenueForm.notes} onChange={(e) => setRevenueForm((p) => ({ ...p, notes: e.target.value }))} placeholder="Нотатки" />
            <div style={{ fontSize: 11, color: 'var(--crm-text-muted)', marginBottom: 8 }}>Коментар для фіксації способу розрахунку або приміток.</div>
            <button onClick={recalculateRevenue} style={{ ...buttonStyle('info'), marginRight: 8 }}>
              Перерахувати з завершених замовлень
            </button>
            <button onClick={saveRevenue} style={buttonStyle('success')}>
              {editingRevenueId !== null ? 'Оновити запис' : 'Зберегти (upsert)'}
            </button>
            {editingRevenueId !== null && (
              <button onClick={cancelEditRevenue} style={{ ...buttonStyle('muted'), marginLeft: 8 }}>
                Скасувати редагування
              </button>
            )}
          </div>
          <div style={{ ...panelStyle, padding: 14 }}>
            <h3 style={{ marginTop: 0 }}>Історія виторгу</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: 8 }}>Період</th>
                  <th style={{ textAlign: 'right', padding: 8 }}>Сума, ₴</th>
                  <th style={{ textAlign: 'center', padding: 8 }}>Режим</th>
                  <th style={{ textAlign: 'center', padding: 8 }}>Дод. послуги</th>
                  <th style={{ textAlign: 'left', padding: 8 }}>Нотатки</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {revenues.map((r) => (
                  <tr key={r.id}>
                    <td style={{ padding: 8 }}>{`${String(r.month).padStart(2, '0')}.${r.year}`}</td>
                    <td style={{ padding: 8, textAlign: 'right' }}>{r.amount.toFixed(2)}</td>
                    <td style={{ padding: 8, textAlign: 'center' }}>{r.isManual ? 'Ручний' : 'Авто'}</td>
                    <td style={{ padding: 8, textAlign: 'center' }}>{r.includeAdditionalServices ? 'Так' : 'Ні'}</td>
                    <td style={{ padding: 8 }}>{r.notes || '—'}</td>
                    <td style={{ padding: 8, textAlign: 'right' }}>
                      <button onClick={() => startEditRevenue(r)} style={{ ...buttonStyle('info'), padding: '6px 10px', marginRight: 8 }}>
                        Редагувати
                      </button>
                      <button onClick={() => deleteRevenue(r.id)} style={{ ...buttonStyle('danger'), padding: '6px 10px' }}>
                        Видалити
                      </button>
                    </td>
                  </tr>
                ))}
                {!revenues.length && (
                <tr><td colSpan={6} style={{ padding: 10, color: 'var(--crm-text-muted)', textAlign: 'center' }}>Дані відсутні</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'attendance' && (
        <div style={{ ...panelStyle, marginTop: 12, padding: 14 }}>
          <div className="hr-attendance-filters" style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
            <select value={gridDepartmentId} onChange={(e) => setGridDepartmentId(e.target.value)}>
              <option value="">Усі підрозділи</option>
              {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            <input type="month" value={gridMonth} onChange={(e) => setGridMonth(e.target.value)} />
            <input
              type="text"
              value={gridSearch}
              onChange={(e) => setGridSearch(e.target.value)}
              placeholder="Пошук працівника"
              style={{ minWidth: 220 }}
            />
            <select value={gridSort} onChange={(e) => setGridSort(e.target.value as typeof gridSort)}>
              <option value="name-asc">Ім'я ↑</option>
              <option value="name-desc">Ім'я ↓</option>
              <option value="hours-desc">Зараховані години ↓</option>
              <option value="hours-asc">Зараховані години ↑</option>
            </select>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--crm-text)' }}>
              <input
                type="checkbox"
                checked={printExpandedAttendanceWithHours}
                onChange={(e) => {
                  setPrintExpandedAttendanceWithHours(e.target.checked);
                  if (e.target.checked) setPrintExpandedAttendanceNoHours(false);
                }}
              />
              Розгорнутий табель з годинами
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--crm-text)' }}>
              <input
                type="checkbox"
                checked={printExpandedAttendanceNoHours}
                onChange={(e) => {
                  setPrintExpandedAttendanceNoHours(e.target.checked);
                  if (e.target.checked) setPrintExpandedAttendanceWithHours(false);
                }}
              />
              Розгорнутий табель без годин
            </label>
            <button
              type="button"
              onClick={printExpandedAttendanceNoHours ? printAttendanceExpandedNoHours : (printExpandedAttendanceWithHours ? () => printHtml(buildAttendancePrintHtml('expanded')) : printAttendanceByMode)}
              style={buttonStyle('success')}
            >
              Друк
            </button>
            <button
              type="button"
              onClick={exportMonthlyAttendanceExcel}
              disabled={!attendanceGrid || visibleGridEmployees.length === 0}
              style={{ ...buttonStyle('success'), opacity: !attendanceGrid || visibleGridEmployees.length === 0 ? 0.6 : 1 }}
            >
              Експорт Excel
            </button>
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
            <div style={{ flex: '1 1 240px', padding: '12px 16px', borderRadius: 'var(--crm-radius-md)', background: 'var(--crm-surface-alt)', border: '1px solid var(--crm-border)', borderLeft: '4px solid var(--crm-info)', color: 'var(--crm-text)' }}>
              <div style={{ fontSize: 13, color: 'var(--crm-text-muted)' }}>Фактично відпрацьовано за місяць</div>
              <strong style={{ display: 'block', marginTop: 4, fontSize: 22, color: 'var(--crm-text)' }}>
                {monthlyAttendanceTotals.actual.toFixed(2)} год
              </strong>
            </div>
            <div style={{ flex: '1 1 240px', padding: '12px 16px', borderRadius: 'var(--crm-radius-md)', background: 'var(--crm-surface-alt)', border: '1px solid var(--crm-border)', borderLeft: '4px solid var(--crm-success)', color: 'var(--crm-text)' }}>
              <div style={{ fontSize: 13, color: 'var(--crm-text-muted)' }}>Зараховано з урахуванням коефіцієнтів</div>
              <strong style={{ display: 'block', marginTop: 4, fontSize: 22, color: 'var(--crm-text)' }}>
                {monthlyAttendanceTotals.weighted.toFixed(2)} год
              </strong>
            </div>
          </div>
          <div className="hr-attendance-grid-scroll" style={{ overflowX: 'auto', overflowY: 'hidden', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', background: 'var(--crm-surface)' }}>
            <table style={{ borderCollapse: 'collapse', minWidth: '1598px', width: 'max-content', tableLayout: 'fixed' }}>
              <thead>
                <tr>
                <th style={{ position: 'sticky', left: 0, background: 'var(--crm-primary)', color: 'var(--crm-primary-contrast)', zIndex: 2, textAlign: 'left', padding: 8, borderBottom: '1px solid var(--crm-border)', width: 280, minWidth: 280, maxWidth: 280 }}>Працівник</th>
                 {attendanceGrid?.days.map((day, index) => (
                   <th
                     key={day}
                     style={{
                       padding: 6,
                       borderBottom: '1px solid var(--crm-border)',
                       fontSize: 11,
                       width: 40,
                       minWidth: 40,
                       maxWidth: 40,
                       background: attendanceDayStyles[index]?.background ?? 'var(--crm-surface)',
                       color: attendanceDayStyles[index]?.textColor ?? 'var(--crm-primary-contrast)',
                       borderLeft: index === 0 ? '1px solid var(--crm-border)' : '1px solid var(--crm-table-row-border)',
                     }}
                   >
                     {new Date(day).getDate()}
                   </th>
                 ))}
                 <th style={{ padding: 6, borderBottom: '1px solid var(--crm-border)', fontSize: 12, width: 78, minWidth: 78, maxWidth: 78, background: 'var(--crm-primary)', color: 'var(--crm-primary-contrast)' }}>Зарах., год</th>
               </tr>
              </thead>
              <tbody>
               {visibleGridEmployees.map((row) => (
                 <tr key={row.appUserId}>
                   <td style={{ position: 'sticky', left: 0, background: 'var(--crm-surface)', color: 'var(--crm-text)', zIndex: 1, padding: 8, borderBottom: '1px solid var(--crm-table-row-border)', whiteSpace: 'nowrap', boxShadow: '2px 0 0 var(--crm-border)' }}>
                     <div>{row.fullName}</div>
                     <div style={{ fontSize: 12, color: 'var(--crm-text-muted)' }}>{row.departmentName || '—'}</div>
                   </td>
                   {row.cells.map((cell, index) => {
                      return (
                        <td key={cell.workDate} style={{ padding: 1, borderBottom: '1px solid var(--crm-table-row-border)', minWidth: 40, width: 40, verticalAlign: 'middle', background: attendanceDayStyles[index]?.background ?? 'var(--crm-surface)', borderLeft: index === 0 ? '1px solid var(--crm-border)' : '1px solid var(--crm-table-row-border)' }}>
                          <button
                            type="button"
                            className="hr-attendance-day-button"
                            onClick={() => openGridCellEditor(row, cell)}
                            style={{
                              width: '100%',
                              minHeight: 14,
                              borderRadius: 'var(--crm-radius-sm)',
                              border: `1px solid ${attendanceDayStyles[index]?.borderColor ?? 'var(--crm-border)'}`,
                              background: cell.hasEntry ? (attendanceDayStyles[index]?.background ?? 'var(--crm-info-bg)') : 'transparent',
                              color: attendanceDayStyles[index]?.textColor ?? 'var(--crm-text)',
                              cursor: 'pointer',
                              fontWeight: 600,
                              fontSize: 10,
                              padding: '0 1px',
                              lineHeight: 1,
                            }}
                            title={cell.hasEntry
                               ? `${cell.entries.length} змін; ${getCellHours(cell)?.toFixed(2) ?? '0.00'} фактичних год; ${getCellWeightedHours(cell).toFixed(2)} зарахованих год`
                              : 'Немає запису'}
                          >
                            {cell.hasEntry ? `${getCellWeightedHours(cell).toFixed(1)}` : '—'}
                          </button>
                        </td>
                      );
                    })}
                    <td style={{ padding: 2, borderBottom: '1px solid var(--crm-table-row-border)', minWidth: 78, width: 78, verticalAlign: 'middle', fontWeight: 700 }}>
                      {totalGridHoursByEmployee.get(row.appUserId)?.toFixed(1) ?? '0.0'}
                    </td>
                  </tr>
                ))}
              </tbody>
           </table>
          </div>
        </div>
      )}

      {editingGridCell && (
        <div className="kitchen-employee-attendance-edit-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div className="kitchen-employee-attendance-edit-dialog" style={{ ...panelStyle, width: 'min(520px, calc(100vw - 24px))', borderRadius: 'var(--crm-radius-lg)', padding: 16, boxShadow: 'var(--crm-shadow-sm)' }}>
            <h3 style={{ marginTop: 0, marginBottom: 8 }}>Табель: {editingGridCell.fullName}</h3>
            <div style={{ fontSize: 13, color: 'var(--crm-text-muted)', marginBottom: 12 }}>{editingGridCell.departmentName} • {editingGridCell.workDate.split('T')[0]}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }} aria-label="Зміни за день">
              {editingGridCell.entries.map((entry, index) => (
                <button
                  key={entry.entryId}
                  type="button"
                  onClick={() => selectGridEditorEntry(entry)}
                  aria-pressed={editingGridCell.entryId === entry.entryId}
                  style={buttonStyle(editingGridCell.entryId === entry.entryId ? 'info' : 'muted')}
                >
                  Зміна {index + 1}: {new Date(entry.checkInAt).toTimeString().slice(0, 5)}–{entry.checkOutAt ? new Date(entry.checkOutAt).toTimeString().slice(0, 5) : 'відкрита'}
                </button>
              ))}
              <button
                type="button"
                onClick={() => selectGridEditorEntry(null)}
                aria-pressed={editingGridCell.entryId === null}
                style={buttonStyle(editingGridCell.entryId === null ? 'info' : 'muted')}
              >
                Додати зміну
              </button>
            </div>
            <div className="kitchen-employee-attendance-edit-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <label style={{ fontSize: 12, color: 'var(--crm-text-muted)' }}>
                Прихід
                <input type="time" value={editingGridCell.checkInAt} onChange={(e) => setEditingGridCell((p) => p ? { ...p, checkInAt: e.target.value } : p)} />
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label htmlFor="hr-grid-check-out" style={{ fontSize: 12, color: 'var(--crm-text-muted)' }}>Вихід</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <input id="hr-grid-check-out" type="time" value={editingGridCell.checkOutAt} onChange={(e) => setEditingGridCell((p) => p ? { ...p, checkOutAt: e.target.value } : p)} />
                  <label style={{ fontSize: 12, color: 'var(--crm-text-muted)' }}>
                    <input
                      type="checkbox"
                      checked={editingGridCell.checkOutNextDay}
                      onChange={(e) => setEditingGridCell((p) => p ? { ...p, checkOutNextDay: e.target.checked } : p)}
                    />{' '}
                    вихід наступного дня
                  </label>
                </div>
              </div>
              <label style={{ fontSize: 12, color: 'var(--crm-text-muted)' }}>
                Коефіцієнт
                <input type="number" step="0.01" value={editingGridCell.hourCoefficient} onChange={(e) => setEditingGridCell((p) => p ? { ...p, hourCoefficient: e.target.value } : p)} />
              </label>
              <label style={{ fontSize: 12, color: 'var(--crm-text-muted)' }}>
                Коментар
                <input value={editingGridCell.comment} onChange={(e) => setEditingGridCell((p) => p ? { ...p, comment: e.target.value } : p)} placeholder="Коментар" />
              </label>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
              <button type="button" onClick={() => setEditingGridCell(null)} style={buttonStyle('muted')}>
                Закрити
              </button>
              <button
                type="button"
                onClick={async () => {
                  const cell = editingGridCell;
                  const saved = await saveGridCell(
                    cell.appUserId,
                    cell.workDate,
                    cell.entryId,
                    cell.checkInAt,
                    cell.checkOutAt,
                    cell.checkOutNextDay,
                    cell.hourCoefficient,
                    cell.comment
                  );
                  if (saved) setEditingGridCell(null);
                }}
                style={buttonStyle('info')}
                disabled={gridSavingCell === `${editingGridCell.appUserId}|${editingGridCell.workDate}`}
              >
                {gridSavingCell === `${editingGridCell.appUserId}|${editingGridCell.workDate}` ? 'Збереження…' : 'Зберегти'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const LegacyHrReadOnlyView: React.FC = () => {
  const [employees, setEmployees] = useState<KitchenEmployeeSummary[]>([]);
  const [details, setDetails] = useState<KitchenEmployeeDetails | null>(null);
  const [error, setError] = useState('');
  const generation = useRef(0);
  const detailRequest = useRef<AbortController | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    void api.get<KitchenEmployeeSummary[]>('/kitchen-employees', { signal: controller.signal })
      .then(response => { if (!controller.signal.aborted) setEmployees(response.data); })
      .catch(failure => {
        if (!controller.signal.aborted)
          setError(extractApiErrorMessage(failure, 'Історичний HR недоступний.'));
      });
    return () => {
      controller.abort();
      detailRequest.current?.abort();
      generation.current += 1;
    };
  }, []);
  const select = async (id: string) => {
    const request = ++generation.current;
    detailRequest.current?.abort();
    const controller = new AbortController();
    detailRequest.current = controller;
    setDetails(null);
    setError('');
    try {
      const response = await api.get<KitchenEmployeeDetails>(
        `/kitchen-employees/${encodeURIComponent(id)}`, { signal: controller.signal });
      if (!controller.signal.aborted && request === generation.current) setDetails(response.data);
    } catch (failure) {
      if (!controller.signal.aborted && request === generation.current)
        setError(extractApiErrorMessage(failure, 'Не вдалося прочитати історичну картку.'));
    }
  };
  return <main className="page-shell restaurant-personnel">
    <h1>Історичний спільний HR — лише читання</h1>
    <p>Це явний перехідний загальносистемний перегляд, не персонал обраного ресторану.
      Після підтвердженого HALLS-переходу цей API закривається.</p>
    <p><a href="?">Повернутися до персоналу ресторану</a></p>
    {error && <p role="alert">{error}</p>}
    <div className="restaurant-personnel__split">
      <aside className="restaurant-personnel__list">
        {employees.map(employee => <button key={employee.id} type="button"
          onClick={() => void select(employee.id)}>{employee.fullName} · {employee.position}</button>)}
      </aside>
      <section>
        {!details && <p>Оберіть історичну картку.</p>}
        {details && <>
          <h2>{details.fullName}</h2>
          <p>{details.position} · {details.departmentName ?? 'Без підрозділу'}</p>
          <p>Коефіцієнт: {details.summaryCoefficient} · Стаж: {details.monthsWorked} міс.</p>
          <p>{details.comment}</p>
          <h3>Історичні інтервали</h3>
          {details.attendance.map(entry => <p key={entry.id}>{entry.checkInAt} — {entry.checkOutAt ?? 'відкрито'}
            {' · '}{entry.hoursWorked} год · {entry.comment}</p>)}
        </>}
      </section>
    </div>
  </main>;
};

const GroupModeLegacyRedirect: React.FC = () => (
  <>
    <section className="page-shell restaurant-personnel" aria-label="Перехід до ресторанних модулів">
      <p role="status">
        Спільні оцінки та загальний виторг недоступні в контексті ресторану.
        Персонал і оцінки ведуться в HR ресторану, а зарплата й виторг — у{' '}
        <a href="/management/restaurant-payroll">зарплаті ресторану</a>.
      </p>
    </section>
    <RestaurantPersonnelPage />
  </>
);

const OperatingContextChangedNotice: React.FC = () => (
  <main className="page-shell restaurant-personnel">
    <p role="alert">{OPERATING_GROUP_CHANGED_IN_ANOTHER_TAB_MESSAGE}</p>
  </main>
);

const OperatingContextPendingNotice: React.FC<{ error?: string | null }> = ({ error }) => (
  <main className="page-shell restaurant-personnel">
    <p role={error ? 'alert' : 'status'}>
      {error || 'Перевіряємо контекст операційної групи. Історичні дані будуть доступні лише в режимі «Усі групи».'}
    </p>
  </main>
);

export const LegacyKitchenEmployeesPage: React.FC = () => {
  const { groupId, changedInAnotherTab } = useSelectedOperatingGroup();
  const context = useOperatingContext();
  const token = useAppSelector(state => state.auth.token);
  if (changedInAnotherTab) return <OperatingContextChangedNotice />;
  if (!isOperatingContextReady(context, token)) return <OperatingContextPendingNotice error={context.error} />;
  return isRestaurantOperatingContext(groupId)
    ? <GroupModeLegacyRedirect />
    : <LegacyKitchenEmployeesPageContent />;
};

const KitchenEmployeesPage: React.FC = () => {
  const { groupId, changedInAnotherTab } = useSelectedOperatingGroup();
  const context = useOperatingContext();
  const token = useAppSelector(state => state.auth.token);
  const legacyHistoryRequested = new URLSearchParams(window.location.search).get('legacy') === '1';

  if (legacyHistoryRequested) {
    if (changedInAnotherTab) return <OperatingContextChangedNotice />;
    if (!isOperatingContextReady(context, token)) return <OperatingContextPendingNotice error={context.error} />;
    if (context.status === 'all' && groupId === 'all') return <LegacyHrReadOnlyView />;
    if (isRestaurantOperatingContext(groupId)) return <GroupModeLegacyRedirect />;
    return <OperatingContextPendingNotice />;
  }
  return <RestaurantPersonnelPage />;
};

export default KitchenEmployeesPage;
